import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { uploadFeatureCatalogImage } from '../../utils/productLicensing';
import { injectAccountLogo, poweredByBlockHtml } from '../message-control-center/layoutCards';
import './HtmlEditor.css';

const TINYMCE_SRC =
  'https://cdn.jsdelivr.net/npm/tinymce@6.8.4/tinymce.min.js';

let editorSeq = 0;
let tinymceLoading = null;

const loadTinyMce = () => {
  if (window.tinymce) {
    return Promise.resolve(window.tinymce);
  }
  if (tinymceLoading) {
    return tinymceLoading;
  }
  tinymceLoading = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-ilocal-tinymce="true"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.tinymce));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = TINYMCE_SRC;
    script.async = true;
    script.setAttribute('data-ilocal-tinymce', 'true');
    script.onload = () => resolve(window.tinymce);
    script.onerror = () => {
      tinymceLoading = null;
      reject(new Error('Unable to load the HTML editor.'));
    };
    document.body.appendChild(script);
  });
  return tinymceLoading;
};

const uploadBlob = (blobInfo, uploader) =>
  uploader(blobInfo.blob()).then((url) => {
    if (!url) {
      throw new Error('Upload did not return an image URL.');
    }
    return url;
  });

const CARD_SELECTOR = '.mcc-layout-card, .mcc-email-outro';

const EMAIL_EXTENDED_ELEMENTS =
  'div[*],span[*],button[*],p[*],a[*],table[*],thead[*],tbody[*],tfoot[*],tr[*],td[*],th[*],img[*],strong[*],em[*],u[*],b[*],i[*],ul[*],ol[*],li[*],h1[*],h2[*],h3[*],h4[*],h5[*],h6[*],hr[*],blockquote[*],font[*],pre[*]';

const EMAIL_VALID_STYLES =
  'color,background,background-color,padding,margin,border,border-radius,border-collapse,table-layout,text-align,width,max-width,min-width,height,max-height,font-weight,font-size,font-family,line-height,display,letter-spacing,text-transform,text-decoration,vertical-align,float,outline,white-space,object-fit,overflow,box-sizing,position,top,left,right,bottom';

const DEFAULT_EDITOR_CONTENT_CSS =
  'body { font-family: Roboto, Helvetica, Arial, sans-serif; font-size: 14px; line-height: 1.6; color: #0f172a; } img { max-width: 100%; height: auto; } .mceNonEditable { background: #e2e8f0; border-radius: 4px; padding: 0 4px; }';

const SMS_EDITOR_CONTENT_CSS = `${DEFAULT_EDITOR_CONTENT_CSS} img.mceNonEditable, div.mceNonEditable { background: transparent; padding: 0; } img[data-token="Account Logo"] { max-width: 200px; max-height: 48px; height: auto; display: block; margin: 8px auto; } div[data-qr-mark] { width: 148px; max-width: 100%; margin: 8px auto; }`;

const EMAIL_IFRAME_LAYOUT_CSS = [
  'html { margin: 0; }',
  'body { margin: 8px; }',
  'table { border-collapse: collapse; }',
  'div.mcc-layout-card { display: block; margin: 0 0 12px; position: relative; }',
  'td.mcc-layout-card:not([data-mcc-size]), th.mcc-layout-card:not([data-mcc-size]), td[data-card="header"]:not([data-mcc-size]), td[data-brand="header"]:not([data-mcc-size]) { display: table-cell !important; margin: 0 !important; width: 100% !important; }',
  'td.stack { display: table-cell !important; }',
  'table[data-mcc-canvas] { width: 100% !important; }',
  'table.wrap { width: 720px !important; min-width: 720px !important; max-width: 720px !important; table-layout: fixed !important; }',
  'table.wrap > tbody > tr > td:only-child:not([data-mcc-size]), table.wrap > tr > td:only-child:not([data-mcc-size]) { width: 720px !important; }',
  'table.wrap > tbody > tr > td:not([data-mcc-size]), table.wrap > tr > td:not([data-mcc-size]) { height: auto !important; vertical-align: top !important; }',
  '[data-card="pickup"], [data-card="pickup_qr"] { height: auto !important; min-height: 0 !important; }',
  '[data-card="header"] { margin: 0 !important; }',
  'table.mcc-layout-card:not([data-mcc-size]) { width: 100% !important; }',
  'table[data-card="hours"] > tbody > tr > td, table[data-card="hours"] > tr > td { vertical-align: top; }',
  'table[data-card="hours"] > tbody > tr > td:not([data-mcc-size]), table[data-card="hours"] > tr > td:not([data-mcc-size]) { width: 50% !important; }',
  'table[data-card="hours"] > tbody > tr > td:only-child, table[data-card="hours"] > tr > td:only-child { width: 100% !important; }',
  'img { max-width: 100%; height: auto; }',
  '[data-pickup-code-chip] { background-color: #F0F2F5 !important; border-radius: 8px !important; padding: 12px 8px !important; margin: 0 0 12px !important; }',
  '[data-pickup-code-chip] p { letter-spacing: 6px !important; font-size: 30px !important; font-weight: 700 !important; line-height: 34px !important; text-align: center !important; color: #1e3a5f !important; }',
  '[data-pickup-code-chip] .mceNonEditable { font-size: 13px !important; letter-spacing: 0 !important; font-weight: 600 !important; line-height: 20px !important; background: #e2e8f0 !important; padding: 0 4px !important; }',
  '[data-mcc-total-due] { display: table !important; width: 100% !important; max-width: 100% !important; min-width: 0 !important; table-layout: auto !important; }',
  '#mcc-resize-box { position: fixed; z-index: 10040; pointer-events: none; box-sizing: border-box; }',
  '#mcc-resize-box > [data-grip] { position: absolute; pointer-events: auto; background: #2563eb; border: 2px solid #ffffff; box-shadow: 0 1px 3px rgba(15,23,42,.35); box-sizing: border-box; }',
  '#mcc-resize-box > [data-grip="e"] { top: 50%; right: -5px; width: 10px; height: 28px; margin-top: -14px; border-radius: 6px; cursor: ew-resize; }',
  '#mcc-resize-box > [data-grip="s"] { left: 50%; bottom: -5px; width: 28px; height: 10px; margin-left: -14px; border-radius: 6px; cursor: ns-resize; }',
  '#mcc-resize-box > [data-grip="se"] { right: -6px; bottom: -6px; width: 12px; height: 12px; border-radius: 3px; cursor: nwse-resize; }',
  '#mcc-resize-box > [data-grip="col"] { top: 0; width: 14px; height: 100%; margin-left: -7px; border: 0; border-radius: 0; background: transparent; box-shadow: none; cursor: col-resize; }',
  '#mcc-resize-box > [data-grip="col"]::before { content: ""; position: absolute; left: 6px; top: 8px; bottom: 8px; width: 2px; background: #2563eb; border-radius: 99px; box-shadow: 0 0 0 3px rgba(37,99,235,.2); }',
  '#mcc-resize-box > [data-size-label] { position: absolute; right: 0; bottom: -26px; background: #0f172a; color: #fff; font: 600 11px/18px Helvetica, Arial, sans-serif; padding: 0 6px; border-radius: 4px; display: none; white-space: nowrap; }',
  'body[data-mcc-resizing] #mcc-resize-box > [data-size-label] { display: block; }',
  'body[data-mcc-resizing] { user-select: none !important; }',
  '[data-mcc-total-due] tbody { display: table-row-group !important; }',
  '[data-mcc-total-due] tr { display: table-row !important; }',
  '[data-mcc-total-due] td { display: table-cell !important; vertical-align: middle !important; }',
  '[data-mcc-total-due] td:first-child { font-size: 14px !important; font-weight: 400 !important; color: #334155 !important; text-align: left !important; width: auto !important; }',
  '[data-mcc-total-due] td:last-child { font-size: 20px !important; font-weight: 700 !important; color: #1e3a5f !important; text-align: right !important; white-space: nowrap !important; width: auto !important; padding-left: 16px !important; }',
  '[data-mcc-total-due] .mceNonEditable { background: transparent !important; padding: 0 !important; font: inherit !important; color: inherit !important; }',
  '[data-mcc-link], span[data-token="Link"] { display: none !important; }',
  'a[data-brand="button"] { color: #ffffff !important; text-decoration: none !important; }',
  '[data-qr-mark] { position: relative !important; width: 148px !important; height: 148px !important; margin: 0 auto !important; }',
  '[data-qr-mark] img[data-qr-src] { width: 148px !important; height: 148px !important; max-width: 148px !important; }',
  '[data-qr-mark] img[data-brand="qr-mark"] { position: absolute !important; left: 50% !important; top: 50% !important; width: 30px !important; height: 40px !important; max-width: 30px !important; margin: -24px 0 0 -19px !important; background: #ffffff !important; border-radius: 6px !important; padding: 4px !important; box-sizing: content-box !important; }',
  '[data-locked-widget="powered_by"] { outline: none !important; }',
  '[data-mcc-theme] { display: none !important; }',
  '[data-mcc-link] { display: none !important; }',
  '[data-card="footer"] { color: #fff; width: 100% !important; }',
  '[data-card="footer"] td { outline: none !important; }',
  '[data-card="footer"] .mceNonEditable { background: transparent; color: inherit; padding: 0; }',
  '[data-card="footer-logo"], [data-locked-widget="powered_by"] img { display: inline; width: 72px; max-width: 72px; height: auto; vertical-align: middle; margin: 0 0 0 4px; }',
  '[data-logo-slot="pharmacy"] { cursor: pointer; }',
  '[data-logo-slot="pharmacy"]:not([data-logo-hint]) { border: 0 !important; outline: none !important; padding: 0 !important; }',
  '[data-logo="pharmacy"] { display: inline-block; width: auto !important; max-height: 36px; max-width: 110px; height: auto; margin: 0; background: #fff; border-radius: 4px; padding: 3px; border: 0 !important; outline: none !important; }',
  'img[data-icon="rx"] { cursor: pointer; }',
  'img[data-logo="account"] { background: #ffffff !important; border-radius: 6px; padding: 6px 10px; max-height: 48px; max-width: 200px; height: auto; width: auto !important; display: block; margin: 0 auto; }',
  '[data-card="header"] .mceNonEditable { background: transparent; color: inherit; padding: 0; }',
  '.mcc-email-outro { display: block; width: 100% !important; margin: 0; }',
  '.mcc-email-outro table.mcc-layout-card { margin: 0 !important; }',
  '.mcc-email-outro, [data-card="footer"], [data-card="branding"] { outline: none !important; }',
  '.mcc-card-tools { position: absolute; top: 6px; right: 6px; z-index: 40; display: none; flex-direction: row; gap: 4px; }',
  '[data-mcc-hover] > .mcc-card-tools, [data-mcc-hover] .mcc-card-tools, [data-mcc-hover] > tbody > tr > td > .mcc-card-tools, [data-mcc-hover] > tr > td > .mcc-card-tools { display: flex !important; }',
  '[data-mcc-hover] { outline: 1px dashed #2563eb !important; outline-offset: 2px; }',
  '[data-mcc-block] { position: relative; }',
  '.mcc-card-tools button, .mcc-card-delete { width: 22px; height: 22px; border: 0; border-radius: 50%; background: #0f172a; color: #fff; cursor: pointer; font-size: 11px; line-height: 22px; text-align: center; padding: 0; box-shadow: 0 1px 3px rgba(15,23,42,.35); }',
  '.mcc-card-handle { cursor: grab; letter-spacing: -1px; }',
  '.mcc-card-delete { background: #be123c !important; }',
  '.mcc-card-delete:hover { background: #9f1239 !important; }',
  '[data-mcc-pay-now-wrap] { text-align: center !important; width: 100% !important; position: relative !important; }',
  '[data-mcc-pay-now-wrap] [data-mcc-pay-note] { text-align: center !important; }',
  '[data-mcc-pay-note] { cursor: text; }',
  'body[data-mcc-sorting] { cursor: grabbing !important; user-select: none !important; }',
  '[data-mcc-dragging] { opacity: 0.35 !important; }',
  '#mcc-drop-line { position: fixed; background: #2563eb; border-radius: 99px; z-index: 10050; pointer-events: none; box-shadow: 0 0 0 3px rgba(37,99,235,.22); }',
].join(' ');

const EMAIL_EDITOR_CONTENT_CSS = `${DEFAULT_EDITOR_CONTENT_CSS} ${EMAIL_IFRAME_LAYOUT_CSS}`;

const FOOTER_CARD_IDS = {
  footer: true,
  branding: true,
  thankyou: true,
  'footer-logo': true,
  'brand-logo': true,
};

const isFooterLayoutNode = (node) => {
  if (!node || !node.getAttribute) {
    return false;
  }
  const card = node.getAttribute('data-card') || '';
  return (
    FOOTER_CARD_IDS[card] ||
    node.getAttribute('data-locked-widget') === 'powered_by' ||
    (node.className &&
      String(node.className).indexOf('mcc-email-outro') !== -1)
  );
};

const wrapTableOf = (body) => {
  if (!body || !body.querySelector) {
    return null;
  }
  const wrap = body.querySelector('table.wrap');
  if (wrap) {
    return wrap;
  }
  const canvas = body.querySelector('table[data-mcc-canvas]');
  return canvas ? canvas.querySelector('table') : null;
};

const wrapHost = (wrap) =>
  wrap && wrap.tBodies && wrap.tBodies[0] ? wrap.tBodies[0] : wrap;

const wrapRowOf = (node, wrap) => {
  if (!node || !wrap) {
    return null;
  }
  let current = node.nodeType === 1 ? node : node.parentNode;
  while (current && current !== wrap) {
    if (current.tagName === 'TR') {
      const parent = current.parentNode;
      if (
        parent === wrap ||
        (parent &&
          parent.parentNode === wrap &&
          /^(TBODY|THEAD|TFOOT)$/.test(parent.tagName || ''))
      ) {
        return current;
      }
    }
    current = current.parentNode;
  }
  return null;
};

const framePoint = (editor, event) => {
  if (!event || typeof event.clientX !== 'number') {
    return null;
  }
  const doc = editor && editor.getDoc ? editor.getDoc() : null;
  const view = doc && doc.defaultView;
  let x = event.clientX;
  let y = event.clientY;
  if (event.view && view && event.view !== view) {
    const iframe = editor.iframeElement;
    if (iframe && iframe.getBoundingClientRect) {
      const rect = iframe.getBoundingClientRect();
      x -= rect.left;
      y -= rect.top;
    }
  }
  return { x, y };
};

const sideForRect = (rect, x, y) => {
  const topBand = rect.top + rect.height * 0.28;
  const bottomBand = rect.bottom - rect.height * 0.28;
  if (y <= topBand) {
    return 'before';
  }
  if (y >= bottomBand) {
    return 'after';
  }
  const left = rect.left + rect.width * 0.28;
  const right = rect.right - rect.width * 0.28;
  if (x < left) {
    return 'left';
  }
  if (x > right) {
    return 'right';
  }
  return y > rect.top + rect.height / 2 ? 'after' : 'before';
};

const isFooterRow = (row) =>
  !!(
    row &&
    row.querySelector &&
    row.querySelector(
      '.mcc-email-outro, [data-card="footer"], [data-card="branding"], [data-card="thankyou"], [data-token="Email Footer"]'
    )
  );

const stackCellAtPoint = (doc, x, y, exclude) => {
  if (!doc || typeof doc.elementFromPoint !== 'function' || typeof x !== 'number') {
    return null;
  }
  const hit = doc.elementFromPoint(x, y);
  if (!hit || !hit.closest) {
    return null;
  }
  const cell = hit.closest('td.stack, th.stack');
  if (!cell || !cell.parentNode) {
    return null;
  }
  const stacks = Array.prototype.filter.call(cell.parentNode.cells || [], (item) =>
    /(^|\s)stack(\s|$)/.test(String(item.className || ''))
  );
  if (stacks.length < 2) {
    return null;
  }
  if (
    exclude &&
    (cell === exclude || cell.contains(exclude) || exclude.contains(cell))
  ) {
    return null;
  }
  return cell;
};

const columnDropTarget = (doc, x, y, exclude) => {
  const column = stackCellAtPoint(doc, x, y, exclude);
  if (!column) {
    return null;
  }
  const rect = column.getBoundingClientRect();
  const band = Math.max(16, rect.height * 0.22);
  if (y < rect.top + band || y > rect.bottom - band) {
    return null;
  }
  return column;
};

const nearestWrapRow = (wrap, x, y, exclude) => {
  const host = wrapHost(wrap);
  if (!host || typeof x !== 'number') {
    return null;
  }
  let best = null;
  let bestDist = Infinity;
  Array.prototype.forEach.call(host.children || [], (row) => {
    if (!row || row.tagName !== 'TR') {
      return;
    }
    if (exclude && (row === exclude || row.contains(exclude))) {
      return;
    }
    const rect = row.getBoundingClientRect();
    if (!rect || rect.height < 1) {
      return;
    }
    const dy =
      y < rect.top ? rect.top - y : y > rect.bottom ? y - rect.bottom : 0;
    const dx =
      x < rect.left - 48 ? rect.left - x : x > rect.right + 48 ? x - rect.right : 0;
    const dist = dy * 3 + dx;
    if (dist < bestDist) {
      bestDist = dist;
      best = { el: row, rect };
    }
  });
  return best;
};

const placeWrapRow = (row, hit, side) => {
  if (!row || !hit || !hit.el || !hit.el.parentNode) {
    return;
  }
  const before = isFooterRow(hit.el) || side === 'before' || side === 'left';
  if (before) {
    hit.el.parentNode.insertBefore(row, hit.el);
    return;
  }
  if (hit.el.nextSibling) {
    hit.el.parentNode.insertBefore(row, hit.el.nextSibling);
    return;
  }
  hit.el.parentNode.appendChild(row);
};

const wrapCellOf = (node, row) => {
  if (!node || !row) {
    return null;
  }
  let current = node;
  while (current && current !== row) {
    if (
      current.parentNode === row &&
      (current.tagName === 'TD' || current.tagName === 'TH')
    ) {
      return current;
    }
    current = current.parentNode;
  }
  return row.cells && row.cells[0] ? row.cells[0] : null;
};

const makeWrapRow = (doc, children, padding) => {
  const row = doc.createElement('tr');
  const cell = doc.createElement('td');
  cell.setAttribute('align', 'left');
  cell.setAttribute('style', `padding:${padding || '12px 24px'};width:720px;`);
  const list = Array.isArray(children) ? children : children ? [children] : [];
  list.forEach((child) => {
    if (child) {
      cell.appendChild(child);
    }
  });
  row.appendChild(cell);
  return row;
};

const setWrapCellWidth = (td, width) => {
  if (!td || !td.style) {
    return;
  }
  td.setAttribute('width', width);
  td.style.setProperty('width', width, 'important');
  td.style.setProperty('display', 'table-cell', 'important');
  td.style.setProperty('vertical-align', 'top');
};

const applyImageHAlign = (img, where) => {
  if (!img || img.nodeName !== 'IMG') {
    return;
  }
  img.style.removeProperty('float');
  img.removeAttribute('align');
  if (where === 'left') {
    img.setAttribute('align', 'left');
    img.style.float = 'left';
    img.style.display = 'inline';
    img.style.margin = '0 16px 8px 0';
    return;
  }
  if (where === 'right') {
    img.setAttribute('align', 'right');
    img.style.float = 'right';
    img.style.display = 'inline';
    img.style.margin = '0 0 8px 16px';
    return;
  }
  img.style.float = 'none';
  img.style.display = 'block';
  img.style.margin = '8px auto';
  const parent = img.parentNode;
  if (parent && (parent.tagName === 'TD' || parent.tagName === 'TH')) {
    parent.setAttribute('align', 'center');
    parent.style.textAlign = 'center';
  }
};

const moveSection = (editor, node, dir) => {
  const body = editor && editor.getBody();
  const doc = editor && editor.getDoc();
  const wrap = wrapTableOf(body);
  if (!body || !doc || !wrap) {
    return false;
  }
  const card = closestLayoutBlock(editor, node);
  const row = wrapRowOf(card || node, wrap);
  if (!row || !row.parentNode) {
    return false;
  }
  const host = row.parentNode;
  const cell = wrapCellOf(card || node, row);
  if (dir === 'up' || dir === 'down') {
    const sib = dir === 'up' ? row.previousElementSibling : row.nextElementSibling;
    if (!sib || sib.tagName !== 'TR') {
      return false;
    }
    if (dir === 'up') {
      host.insertBefore(row, sib);
    } else {
      host.insertBefore(sib, row);
    }
    return true;
  }
  if (dir === 'left' || dir === 'right') {
    if (row.cells.length > 1 && cell) {
      const sib = dir === 'left' ? cell.previousElementSibling : cell.nextElementSibling;
      if (sib && (sib.tagName === 'TD' || sib.tagName === 'TH')) {
        if (dir === 'left') {
          row.insertBefore(cell, sib);
        } else {
          row.insertBefore(sib, cell);
        }
        return true;
      }
      const newRow = doc.createElement('tr');
      newRow.appendChild(cell);
      setWrapCellWidth(cell, '720px');
      if (dir === 'left') {
        host.insertBefore(newRow, row);
      } else if (row.nextSibling) {
        host.insertBefore(newRow, row.nextSibling);
      } else {
        host.appendChild(newRow);
      }
      if (row.cells.length === 1) {
        setWrapCellWidth(row.cells[0], '720px');
      }
      return true;
    }
    const neighbor = dir === 'left' ? row.previousElementSibling : row.nextElementSibling;
    if (!neighbor || neighbor.tagName !== 'TR' || !cell) {
      return false;
    }
    if (dir === 'left') {
      neighbor.appendChild(cell);
    } else if (neighbor.cells[0]) {
      neighbor.insertBefore(cell, neighbor.cells[0]);
    } else {
      neighbor.appendChild(cell);
    }
    if (!row.cells.length && row.parentNode) {
      row.parentNode.removeChild(row);
    }
    Array.prototype.forEach.call(neighbor.cells, (td) => {
      setWrapCellWidth(td, `${Math.floor(100 / neighbor.cells.length)}%`);
      td.setAttribute('valign', 'top');
    });
    return true;
  }
  if (dir === 'center' && row.cells.length > 1) {
    const cells = Array.prototype.slice.call(row.cells);
    cells.forEach((td, index) => {
      setWrapCellWidth(td, '720px');
      if (index === 0) {
        return;
      }
      const newRow = doc.createElement('tr');
      newRow.appendChild(td);
      if (row.nextSibling) {
        host.insertBefore(newRow, row.nextSibling);
      } else {
        host.appendChild(newRow);
      }
    });
    return true;
  }
  return false;
};

const applyPlace = (editor, where) => {
  const node = editor.selection && editor.selection.getNode();
  if (!node) {
    return;
  }
  const img =
    node.nodeName === 'IMG' ? node : editor.dom.getParent(node, 'img');
  const cell = editor.dom.getParent(node, 'td,th');
  if (where === 'top' || where === 'middle' || where === 'bottom') {
    if (cell) {
      cell.setAttribute('valign', where);
      cell.style.verticalAlign = where;
    }
    return;
  }
  if (img) {
    applyImageHAlign(img, where);
    return;
  }
  const isText = /^(P|H1|H2|H3|H4|H5|H6|SPAN|STRONG|EM|LI|FONT)$/.test(
    node.nodeName
  );
  if (isText || (cell && editor.dom.getParent(node, 'p'))) {
    const block =
      editor.dom.getParent(node, 'p,h1,h2,h3,h4,h5,h6,div,td,th') || cell;
    if (block) {
      block.setAttribute('align', where);
      editor.dom.setStyle(block, 'text-align', where);
    }
    return;
  }
  moveSection(editor, node, where);
};

const injectEmailLayoutCss = (editor) => {
  const doc = editor && editor.getDoc();
  if (!doc) {
    return;
  }
  let style = doc.getElementById('mcc-email-layout');
  if (!style) {
    style = doc.createElement('style');
    style.id = 'mcc-email-layout';
    style.type = 'text/css';
    const parent = doc.head || doc.documentElement;
    parent.appendChild(style);
  }
  if (style.textContent !== EMAIL_IFRAME_LAYOUT_CSS) {
    style.textContent = EMAIL_IFRAME_LAYOUT_CSS;
  }
};

const orderFooterStack = (body, wrap) => {
  if (!body || !wrap) {
    return;
  }
  const doc = body.ownerDocument;
  const host = wrapHost(wrap);
  if (!host || !doc) {
    return;
  }
  let outro = body.querySelector('.mcc-email-outro, [data-token="Email Footer"]');
  if (!outro) {
    outro = doc.createElement('div');
    outro.className = 'mcc-email-outro';
    outro.setAttribute('data-token', 'Email Footer');
  }
  Array.prototype.forEach.call(
    body.querySelectorAll('[data-card="footer_note"], [data-region="footer_note"]'),
    (node) => {
      let target = node;
      let current = node;
      while (current) {
        if (
          current.getAttribute &&
          current.getAttribute('data-card') === 'footer_note'
        ) {
          target = current;
          break;
        }
        current = current.parentNode;
      }
      if (target && target.parentNode) {
        target.parentNode.removeChild(target);
      }
    }
  );
  ['thankyou', 'footer', 'branding'].forEach((id) => {
    const node = body.querySelector(`[data-card="${id}"]`);
    if (!node) {
      return;
    }
    if (node.parentNode !== outro) {
      outro.appendChild(node);
    }
  });
  if (!wrap.contains(outro)) {
    host.appendChild(makeWrapRow(doc, outro, '0'));
  } else {
    const row = wrapRowOf(outro, wrap);
    if (
      row &&
      row.parentNode &&
      row.parentNode.lastElementChild !== row
    ) {
      row.parentNode.appendChild(row);
    }
  }
  Array.prototype.forEach.call(
    host.querySelectorAll('tr'),
    (row) => {
      const cell = row.cells && row.cells[0];
      if (
        row.cells &&
        row.cells.length === 1 &&
        cell &&
        !String(cell.innerHTML || '').trim()
      ) {
        if (row.parentNode) {
          row.parentNode.removeChild(row);
        }
      }
    }
  );
};

const restorePoweredBy = (editor) => {
  const body = editor && editor.getBody();
  if (!body || body.querySelector('[data-locked-widget="powered_by"]')) {
    return;
  }
  const html = poweredByBlockHtml();
  const outro = body.querySelector('.mcc-email-outro, [data-token="Email Footer"]');
  if (outro) {
    outro.insertAdjacentHTML('beforeend', html);
    return;
  }
  body.insertAdjacentHTML('beforeend', html);
};

const forceEmailCanvasDom = (editor) => {
  const body = editor && editor.getBody();
  const doc = editor && editor.getDoc();
  if (!body || !doc) {
    return;
  }
  injectEmailLayoutCss(editor);
  Array.prototype.forEach.call(body.querySelectorAll('colgroup, col'), (node) => {
    if (node.parentNode) {
      node.parentNode.removeChild(node);
    }
  });
  const wrap = wrapTableOf(body);
  const canvas = body.querySelector('table[data-mcc-canvas]');
  const setImp = (el, prop, value) => {
    if (
      el.style.getPropertyValue(prop) === value &&
      el.style.getPropertyPriority(prop) === 'important'
    ) {
      return;
    }
    el.style.setProperty(prop, value, 'important');
  };
  if (wrap) {
    if (wrap.getAttribute('width') !== '720') {
      wrap.setAttribute('width', '720');
    }
    setImp(wrap, 'width', '720px');
    setImp(wrap, 'min-width', '720px');
    setImp(wrap, 'max-width', '720px');
    setImp(wrap, 'table-layout', 'fixed');
    Array.prototype.forEach.call(wrap.rows || [], (row) => {
      const cells = row.cells || [];
      const single = cells.length <= 1;
      Array.prototype.forEach.call(cells, (td) => {
        setImp(td, 'display', 'table-cell');
        if (td.getAttribute('data-mcc-size') === '1') {
          return;
        }
        setImp(td, 'width', single ? '720px' : `${Math.floor(100 / cells.length)}%`);
        setImp(td, 'height', 'auto');
      });
    });
  }
  if (canvas) {
    setImp(canvas, 'width', '100%');
  }
  Array.prototype.forEach.call(
    body.querySelectorAll('table.mcc-layout-card:not([data-mcc-size])'),
    (table) => {
      setImp(table, 'width', '100%');
    }
  );
  Array.prototype.forEach.call(body.querySelectorAll('[data-mcc-total-due]'), (table) => {
    table.style.removeProperty('min-width');
    setImp(table, 'max-width', '100%');
    setImp(table, 'min-width', '0');
    setImp(table, 'width', '100%');
    setImp(table, 'table-layout', 'auto');
    setImp(table, 'display', 'table');
    Array.prototype.forEach.call(table.rows || [], (row) => {
      const cells = row.cells || [];
      if (cells.length < 2) {
        return;
      }
      setImp(cells[0], 'text-align', 'left');
      setImp(cells[0], 'font-size', '14px');
      setImp(cells[0], 'font-weight', '400');
      setImp(cells[0], 'color', '#334155');
      setImp(cells[0], 'width', 'auto');
      const amount = cells[cells.length - 1];
      setImp(amount, 'text-align', 'right');
      setImp(amount, 'font-size', '20px');
      setImp(amount, 'font-weight', '700');
      setImp(amount, 'color', '#1e3a5f');
      setImp(amount, 'white-space', 'nowrap');
      setImp(amount, 'width', 'auto');
      amount.removeAttribute('width');
    });
  });
  Array.prototype.forEach.call(
    body.querySelectorAll('td.mcc-layout-card, td[data-card="header"]'),
    (td) => {
      setImp(td, 'display', 'table-cell');
      if (
        td.getAttribute('data-mcc-size') !== '1' &&
        td.parentNode &&
        td.parentNode.cells &&
        td.parentNode.cells.length === 1
      ) {
        setImp(td, 'width', '100%');
      }
      setImp(td, 'margin', '0');
    }
  );
  if (!wrap) {
    return;
  }
  const host = wrapHost(wrap);
  const moveIntoWrap = (el, padding) => {
    if (!el || wrap.contains(el)) {
      return;
    }
    host.appendChild(makeWrapRow(doc, el, padding));
  };
  const outro = body.querySelector('.mcc-email-outro, [data-token="Email Footer"]');
  if (outro && !wrap.contains(outro)) {
    moveIntoWrap(outro, '0');
  }
  Array.prototype.slice
    .call(body.querySelectorAll('table[data-card], div[data-card]'))
    .forEach((el) => {
      if (wrap.contains(el) || (outro && outro.contains(el))) {
        return;
      }
      if (el.getAttribute && el.getAttribute('data-mcc-canvas')) {
        return;
      }
      let parent = el.parentNode;
      while (parent && parent !== body) {
        if (
          parent.getAttribute &&
          parent.getAttribute('data-card') &&
          parent !== el
        ) {
          return;
        }
        if (
          parent.classList &&
          parent.classList.contains('mcc-email-outro')
        ) {
          return;
        }
        parent = parent.parentNode;
      }
      moveIntoWrap(el, isFooterLayoutNode(el) ? '0' : '12px 24px');
    });
  orderFooterStack(body, wrap);
};

const tinymcePluginsFor = (preset, compact) => {
  if (compact || preset === 'sms') {
    return 'lists link autolink';
  }
  if (preset === 'email') {
    return 'searchreplace autolink directionality code visualblocks visualchars image link media table charmap lists advlist wordcount help emoticons';
  }
  return 'preview searchreplace autolink directionality code visualblocks visualchars fullscreen image link media table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount help emoticons quickbars';
};

const PHARMACY_LOGO_STYLE =
  'display:inline-block;max-height:36px;max-width:110px;width:auto;height:auto;border:0;outline:none;background:#ffffff;border-radius:4px;padding:3px;';

const PHARMACY_LOGO_HINT =
  'Upload logo';

const isUsableImageSrc = (src) => {
  const value = String(src || '').trim();
  if (!value) {
    return false;
  }
  if (/^data:image\/gif;base64,R0lGODlhAQABAIAAAAAAAP/i.test(value)) {
    return false;
  }
  return true;
};

const collectImageSrcs = (body) => {
  const srcs = [];
  if (!body || !body.querySelectorAll) {
    return srcs;
  }
  Array.prototype.forEach.call(body.querySelectorAll('img'), (img) => {
    srcs.push(img.getAttribute('src') || '');
  });
  return srcs;
};

const focusImageUploadTab = () => {
  window.setTimeout(() => {
    const tabs = document.querySelectorAll('.tox-dialog .tox-tab');
    Array.prototype.forEach.call(tabs, (tab) => {
      if (/upload/i.test(String(tab.textContent || ''))) {
        tab.click();
      }
    });
  }, 0);
};

const applyPharmacyLogoPick = (editor, pick) => {
  const body = editor && editor.getBody();
  if (!body) {
    return;
  }
  const slot =
    pick && pick.slot && pick.slot.parentNode
      ? pick.slot
      : body.querySelector('[data-logo-slot="pharmacy"]');
  if (!slot) {
    return;
  }
  const before = (pick && pick.srcs) || [];
  let img = slot.querySelector('img');
  if (!img || !isUsableImageSrc(img.getAttribute('src'))) {
    Array.prototype.forEach.call(body.querySelectorAll('img'), (el) => {
      if (el.getAttribute('data-card') === 'footer-logo') {
        return;
      }
      const src = el.getAttribute('src') || '';
      if (isUsableImageSrc(src) && before.indexOf(src) === -1) {
        img = el;
      }
    });
  }
  if (!img || !isUsableImageSrc(img.getAttribute('src'))) {
    if (pick && pick.hadSrc) {
      return;
    }
    slot.innerHTML = PHARMACY_LOGO_HINT;
    slot.setAttribute('data-logo-hint', '1');
    return;
  }
  if (img.parentNode !== slot) {
    slot.innerHTML = '';
    slot.appendChild(img);
  } else {
    Array.prototype.forEach.call(
      slot.querySelectorAll('[data-logo-hint]'),
      (hint) => {
        if (hint.parentNode) {
          hint.parentNode.removeChild(hint);
        }
      }
    );
  }
  editor.dom.setAttribs(img, {
    'data-logo': 'pharmacy',
    'data-card': 'brand-logo',
    alt: 'Pharmacy logo',
    width: '110',
    height: '36',
  });
  img.setAttribute('style', PHARMACY_LOGO_STYLE);
  img.setAttribute('data-mce-style', PHARMACY_LOGO_STYLE);
  slot.removeAttribute('data-logo-hint');
  const slotStyle =
    'display:inline-block;vertical-align:middle;margin-right:10px;border:0;padding:0;';
  slot.setAttribute('style', slotStyle);
  slot.setAttribute('data-mce-style', slotStyle);
};

const isProtectedBrandImage = (node) => {
  if (!node || !node.closest) {
    return false;
  }
  if (
    node.closest(
      '[data-locked-widget="powered_by"], [data-card="branding"], [data-card="footer-logo"], [data-logo-slot="pharmacy"]'
    )
  ) {
    return true;
  }
  const logo = node.getAttribute && node.getAttribute('data-logo');
  return logo === 'pharmacy' || node.getAttribute('data-card') === 'footer-logo';
};

const attachImageDeleteButton = (editor) => {
  const doc = editor.getDoc();
  const body = editor.getBody();
  if (!doc || !body || doc.getElementById('mcc-img-delete')) {
    return;
  }
  const btn = doc.createElement('button');
  btn.id = 'mcc-img-delete';
  btn.type = 'button';
  btn.setAttribute('data-mce-bogus', 'all');
  btn.setAttribute('contenteditable', 'false');
  btn.setAttribute('title', 'Remove image');
  btn.appendChild(doc.createTextNode('\u00d7'));
  btn.style.cssText =
    'position:fixed;z-index:10060;display:none;width:22px;height:22px;border:0;border-radius:50%;background:#be123c;color:#fff;cursor:pointer;font:600 14px/22px Helvetica,Arial,sans-serif;text-align:center;padding:0;box-shadow:0 1px 3px rgba(15,23,42,.35);';
  body.appendChild(btn);
  let current = null;
  const hide = () => {
    btn.style.display = 'none';
    current = null;
  };
  const place = (img) => {
    const rect = img.getBoundingClientRect();
    if (!rect.width && !rect.height) {
      hide();
      return;
    }
    btn.style.display = 'block';
    btn.style.top = `${Math.max(4, rect.top + 4)}px`;
    btn.style.left = `${Math.max(4, rect.right - 26)}px`;
    current = img;
  };
  editor.on('mouseover', (event) => {
    const target = event.target;
    if (!target || !target.closest) {
      return;
    }
    if (target.id === 'mcc-img-delete' || target.closest('#mcc-img-delete')) {
      return;
    }
    const img = target.closest('img');
    if (!img || isProtectedBrandImage(img)) {
      return;
    }
    place(img);
  });
  editor.on('mouseout', (event) => {
    const next = event.relatedTarget;
    if (
      next &&
      next.closest &&
      next.closest('#mcc-img-delete, img')
    ) {
      return;
    }
    hide();
  });
  const removeImage = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!current || !current.parentNode) {
      hide();
      return;
    }
    const qr =
      current.closest &&
      current.closest('[data-qr-mark], [data-token="QR Code"]');
    const node = qr && qr !== body ? qr : current;
    editor.undoManager.transact(() => {
      if (node.parentNode) {
        node.parentNode.removeChild(node);
      }
    });
    editor.nodeChanged();
    editor.fire('change');
    editor.fire('input');
    hide();
  };
  btn.addEventListener('mousedown', (event) => {
    event.preventDefault();
    event.stopPropagation();
  });
  btn.addEventListener('click', removeImage);
};

const isPoweredByLock = (node) => {
  if (!node || node.nodeType !== 1 || !node.getAttribute) {
    return false;
  }
  return (
    node.getAttribute('data-locked-widget') === 'powered_by' ||
    node.getAttribute('data-card') === 'branding' ||
    node.getAttribute('data-card') === 'footer-logo' ||
    node.getAttribute('data-logo') === 'pharmacy' ||
    node.getAttribute('data-logo') === 'account' ||
    node.getAttribute('data-logo-slot') === 'pharmacy'
  );
};

const isLockedLayoutCard = (node) => {
  if (!node || node.nodeType !== 1 || !node.getAttribute) {
    return false;
  }
  const card = node.getAttribute('data-card');
  return (
    node.getAttribute('data-locked-widget') === 'powered_by' ||
    card === 'branding' ||
    card === 'footer-logo' ||
    card === 'brand-logo'
  );
};

const nodeTouchesPoweredBy = (node, body) => {
  let current = node;
  while (current && current !== body) {
    if (isPoweredByLock(current)) {
      return true;
    }
    current = current.parentNode;
  }
  return false;
};

const closestLayoutBlock = (editor, node) => {
  if (!node || !editor || !editor.dom) {
    return null;
  }
  let current = node.nodeType === 3 ? node.parentNode : node;
  while (current && current !== editor.getBody()) {
    if (
      current.nodeType === 1 &&
      current.className &&
      /(^|\s)(mcc-layout-card|mcc-email-outro)(\s|$)/.test(
        String(current.className)
      )
    ) {
      return current;
    }
    current = current.parentNode;
  }
  return editor.dom.getParent(node, CARD_SELECTOR);
};

const DRAGGABLE_SELECTOR =
  '.mcc-layout-card, [data-mcc-pay-now-wrap], [data-mcc-block], [data-mcc-total-due]';

const eventEl = (event) => {
  const target = event && event.target;
  if (!target) {
    return null;
  }
  return target.nodeType === 3 ? target.parentNode : target;
};

const iconRowOf = (node, body) => {
  let current = node && node.nodeType === 3 ? node.parentNode : node;
  while (current && current !== body) {
    if (current.nodeType === 1 && current.getAttribute('data-mcc-icon-row')) {
      return current;
    }
    current = current.parentNode;
  }
  return null;
};

const hoverCardOf = (node, body) => {
  let current = node && node.nodeType === 3 ? node.parentNode : node;
  while (current && current !== body) {
    if (current.nodeType === 1) {
      const cls = String(current.className || '');
      if (
        /(^|\s)mcc-layout-card(\s|$)/.test(cls) ||
        current.getAttribute('data-mcc-pay-now-wrap') ||
        current.getAttribute('data-mcc-total-due') ||
        current.getAttribute('data-mcc-block')
      ) {
        if (!isFooterLayoutNode(current) && !isLockedLayoutCard(current)) {
          return current;
        }
      }
    }
    current = current.parentNode;
  }
  return null;
};

const closestDraggable = (node, body) => {
  const handle = node && node.closest ? node.closest('.mcc-card-handle') : null;
  if (handle) {
    let owner = handle.parentNode;
    while (owner && owner !== body) {
      if (owner.nodeType === 1) {
        const cls = String(owner.className || '');
        if (
          /(^|\s)mcc-layout-card(\s|$)/.test(cls) ||
          owner.getAttribute('data-mcc-pay-now-wrap') ||
          owner.getAttribute('data-mcc-total-due')
        ) {
          if (!isFooterLayoutNode(owner) && !isLockedLayoutCard(owner)) {
            return owner;
          }
        }
      }
      owner = owner.parentNode;
    }
  }
  const iconRow = iconRowOf(node, body);
  if (iconRow) {
    return iconRow;
  }
  const column = pairedStackOf(node, body);
  if (column && !(handle && column.contains(handle))) {
    return column;
  }
  let current = node && node.nodeType === 3 ? node.parentNode : node;
  while (current && current !== body) {
    if (current.nodeType === 1) {
      const cls = String(current.className || '');
      if (/(^|\s)mcc-card-tools(\s|$)/.test(cls)) {
        current = current.parentNode;
        continue;
      }
      if (isFooterLayoutNode(current) || isLockedLayoutCard(current)) {
        current = current.parentNode;
        continue;
      }
      if (
        /(^|\s)mcc-layout-card(\s|$)/.test(cls) ||
        current.getAttribute('data-mcc-pay-now-wrap') ||
        current.getAttribute('data-mcc-block') ||
        current.getAttribute('data-mcc-total-due')
      ) {
        return current;
      }
    }
    current = current.parentNode;
  }
  return null;
};

const INNER_BLOCK_SKIP =
  '.mcc-card-tools, [data-mce-bogus], [data-mcc-ghost], [data-mcc-theme], [data-logo-slot], .mceNonEditable, #mcc-drop-line';

const markInnerBlocks = (body) => {
  Array.prototype.forEach.call(
    body.querySelectorAll('.mcc-layout-card table, .mcc-layout-card div'),
    (el) => {
      if (
        el.getAttribute('data-mcc-block') ||
        el.getAttribute('data-mcc-total-due') ||
        el.getAttribute('data-mcc-pay-now-wrap') ||
        /(^|\s)mcc-layout-card(\s|$)/.test(String(el.className || '')) ||
        el.matches(INNER_BLOCK_SKIP) ||
        el.closest('.mcc-email-outro, [data-locked-widget]') ||
        el.querySelector('.mcc-layout-card')
      ) {
        return;
      }
      const parent = el.parentNode;
      const parentIsSlot =
        parent &&
        (parent.nodeName === 'TD' ||
          /(^|\s)mcc-layout-card(\s|$)/.test(String(parent.className || '')));
      if (!parentIsSlot) {
        return;
      }
      const text = String(el.textContent || '').replace(/[\s\u00a0]+/g, '');
      if (!text && !el.querySelector('img')) {
        return;
      }
      el.setAttribute('data-mcc-block', '1');
    }
  );
  Array.prototype.forEach.call(
    body.querySelectorAll('table.mcc-layout-card > tbody > tr, table.mcc-layout-card > tr'),
    (row) => {
      if (row.closest && row.closest('.mcc-email-outro, [data-locked-widget]')) {
        return;
      }
      const cells = Array.prototype.filter.call(row.cells || [], (cell) => {
        const text = String(cell.textContent || '').replace(/[\s\u00a0]+/g, '');
        return text || cell.querySelector('img, table');
      });
      if (cells.length < 2) {
        return;
      }
      cells.forEach((cell) => {
        if (!/(^|\s)stack(\s|$)/.test(String(cell.className || ''))) {
          cell.className = `${cell.className || ''} stack`.replace(/^\s+/, '');
        }
        cell.removeAttribute('data-mcc-block');
      });
    }
  );
  Array.prototype.forEach.call(body.querySelectorAll('img'), (img) => {
    const src = img.getAttribute('src') || '';
    const isRx =
      img.getAttribute('data-icon') === 'rx' || /\/email-icons\/rx\.png(?:$|\?)/i.test(src);
    if (!isRx) {
      return;
    }
    img.setAttribute('data-icon', 'rx');
    if (!img.getAttribute('data-icon-default') && /\/email-icons\/rx\.png(?:$|\?)/i.test(src)) {
      img.setAttribute('data-icon-default', src);
    }
    const table = img.closest && img.closest('table');
    if (
      table &&
      !/(^|\s)mcc-layout-card(\s|$)/.test(String(table.className || '')) &&
      !table.getAttribute('data-mcc-total-due')
    ) {
      table.setAttribute('data-mcc-block', '1');
      table.setAttribute('data-mcc-icon-row', 'rx');
    }
  });
};

const pairedStackOf = (node, body) => {
  let current = node && node.nodeType === 3 ? node.parentNode : node;
  while (current && current !== body) {
    if (
      current.nodeType === 1 &&
      current.nodeName === 'TD' &&
      /(^|\s)stack(\s|$)/.test(String(current.className || ''))
    ) {
      const row = current.parentNode;
      const count = row
        ? Array.prototype.filter.call(row.cells || [], (cell) =>
            /(^|\s)stack(\s|$)/.test(String(cell.className || ''))
          ).length
        : 0;
      if (count >= 2 && !isFooterLayoutNode(current) && !isLockedLayoutCard(current)) {
        return current;
      }
    }
    current = current.parentNode;
  }
  return null;
};

const columnCellsOf = (card) => {
  if (!card || card.nodeName !== 'TABLE' || !card.rows || !card.rows.length) {
    return [];
  }
  const row = card.rows[0];
  return Array.prototype.filter.call(row.cells || [], (cell) =>
    /(^|\s)stack(\s|$)/.test(String(cell.className || ''))
  );
};

const trackHoverBlock = (editor) => {
  const body = editor.getBody();
  if (!body || body.getAttribute('data-mcc-hover-track') === '1') {
    return;
  }
  body.setAttribute('data-mcc-hover-track', '1');
  const doc = editor.getDoc();
  const win = doc.defaultView || window;
  let current = null;
  let resize = null;

  const resizeBox = (create) => {
    let box = doc.getElementById('mcc-resize-box');
    if (!box && create) {
      box = doc.createElement('div');
      box.id = 'mcc-resize-box';
      box.setAttribute('data-mce-bogus', 'all');
      box.setAttribute('contenteditable', 'false');
      ['e', 's', 'se'].forEach((dir) => {
        const grip = doc.createElement('div');
        grip.setAttribute('data-grip', dir);
        grip.setAttribute('title', dir === 'e' ? 'Drag to resize width' : dir === 's' ? 'Drag to resize height' : 'Drag to resize');
        box.appendChild(grip);
      });
      const label = doc.createElement('div');
      label.setAttribute('data-size-label', '1');
      box.appendChild(label);
      body.appendChild(box);
    }
    return box;
  };

  const placeBox = () => {
    const box = resizeBox(Boolean(current));
    if (!box) {
      return;
    }
    if (!current || !current.isConnected) {
      box.style.display = 'none';
      return;
    }
    const rect = current.getBoundingClientRect();
    box.style.display = 'block';
    box.style.left = `${rect.left}px`;
    box.style.top = `${rect.top}px`;
    box.style.width = `${rect.width}px`;
    box.style.height = `${rect.height}px`;
    Array.prototype.slice
      .call(box.querySelectorAll('[data-grip="col"]'))
      .forEach((grip) => {
        if (grip.parentNode) {
          grip.parentNode.removeChild(grip);
        }
      });
    const cells = columnCellsOf(current);
    if (cells.length >= 2) {
      cells.slice(0, -1).forEach((cell, index) => {
        const edge = cell.getBoundingClientRect().right - rect.left;
        const grip = doc.createElement('div');
        grip.setAttribute('data-grip', 'col');
        grip.setAttribute('data-col-index', String(index));
        grip.setAttribute('title', 'Drag to resize the columns');
        grip.style.left = `${edge}px`;
        box.appendChild(grip);
      });
    }
  };

  const setHover = (node) => {
    if (node === current) {
      return;
    }
    if (current) {
      current.removeAttribute('data-mcc-hover');
    }
    current = node;
    if (current) {
      current.setAttribute('data-mcc-hover', '1');
    }
    placeBox();
  };

  const slotWidth = (node) => {
    const parent = node.parentNode;
    if (!parent || parent.nodeType !== 1) {
      return node.getBoundingClientRect().width || 1;
    }
    const style = win.getComputedStyle(parent);
    const pad = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
    return Math.max(1, parent.getBoundingClientRect().width - pad);
  };

  const naturalHeight = (node) => {
    const prev = node.style.getPropertyValue('height');
    const prio = node.style.getPropertyPriority('height');
    node.style.setProperty('height', 'auto', 'important');
    const height = node.getBoundingClientRect().height;
    if (prev) {
      node.style.setProperty('height', prev, prio);
    } else {
      node.style.removeProperty('height');
    }
    return height;
  };

  const syncMceStyle = (node) => {
    const cleaned = String(node.getAttribute('style') || '')
      .replace(/\s*!important/gi, '')
      .replace(/;{2,}/g, ';')
      .replace(/^\s*;|;\s*$/g, '');
    if (cleaned) {
      node.setAttribute('style', cleaned);
      node.setAttribute('data-mce-style', cleaned);
    }
  };

  const writeWidth = (el, pct) => {
    el.setAttribute('data-mcc-size', '1');
    el.style.removeProperty('width');
    el.style.removeProperty('min-width');
    el.style.setProperty('width', `${pct}%`);
    if (el.nodeName === 'TD' || el.nodeName === 'TABLE') {
      el.setAttribute('width', `${pct}%`);
    }
    syncMceStyle(el);
  };

  const siblingStacks = (node) => {
    if (
      !node ||
      node.nodeName !== 'TD' ||
      !/(^|\s)stack(\s|$)/.test(String(node.className || '')) ||
      !node.parentNode
    ) {
      return null;
    }
    const others = Array.prototype.filter.call(node.parentNode.cells || [], (cell) =>
      cell !== node && /(^|\s)stack(\s|$)/.test(String(cell.className || ''))
    );
    return others.length ? others : null;
  };

  const applySize = (node, widthPct, heightPx) => {
    const siblings = siblingStacks(node);
    if (widthPct != null && siblings) {
      const min = 20;
      const next = Math.max(min, Math.min(100 - min * siblings.length, widthPct));
      writeWidth(node, next);
      let used = next;
      siblings.forEach((cell, index) => {
        const pct =
          index === siblings.length - 1
            ? 100 - used
            : Math.floor((100 - next) / siblings.length);
        used += pct;
        writeWidth(cell, pct);
      });
      const table = node.closest && node.closest('table');
      if (table) {
        table.style.setProperty('table-layout', 'fixed');
        syncMceStyle(table);
      }
    } else if (widthPct != null) {
      node.setAttribute('data-mcc-size', '1');
      node.style.removeProperty('width');
      node.style.removeProperty('min-width');
      node.style.setProperty('width', `${widthPct}%`);
      node.style.setProperty('max-width', '100%');
      if (node.nodeName === 'TABLE') {
        node.setAttribute('width', `${widthPct}%`);
      }
      syncMceStyle(node);
    }
    if (heightPx != null) {
      node.style.removeProperty('height');
      node.style.setProperty('height', `${heightPx}px`);
      if (node.nodeName === 'TD' || node.nodeName === 'TABLE') {
        node.setAttribute('height', String(heightPx));
      }
      syncMceStyle(node);
    }
  };

  let resizeSync = null;
  const syncPreview = (wait) => {
    if (resizeSync) {
      window.clearTimeout(resizeSync);
    }
    resizeSync = window.setTimeout(() => {
      resizeSync = null;
      editor.undoManager.add();
      editor.nodeChanged();
      editor.fire('change');
    }, wait);
  };

  const onResizeMove = (event) => {
    if (!resize) {
      return;
    }
    event.preventDefault();
    const dx = event.clientX - resize.x;
    const dy = event.clientY - resize.y;
    if (resize.dir === 'col') {
      const min = resize.total * 0.15;
      const max = resize.total - min;
      const next = Math.max(min, Math.min(max, resize.leftPx + dx));
      const leftPct = Math.max(15, Math.min(85, Math.round((next / resize.total) * 100)));
      writeWidth(resize.node, leftPct);
      writeWidth(resize.pair, 100 - leftPct);
      const table = resize.node.closest && resize.node.closest('table');
      if (table) {
        table.style.setProperty('table-layout', 'fixed');
        syncMceStyle(table);
      }
      placeBox();
      const label = resizeBox(true).querySelector('[data-size-label]');
      if (label) {
        label.textContent = `${leftPct}% / ${100 - leftPct}%`;
      }
      syncPreview(120);
      return;
    }
    let widthPct = null;
    let heightPx = null;
    if (resize.dir !== 's') {
      const px = Math.max(40, resize.w + dx);
      widthPct = Math.max(8, Math.min(100, Math.round((px / resize.slot) * 100)));
    }
    if (resize.dir !== 'e') {
      heightPx = Math.max(Math.ceil(resize.minH), Math.round(resize.h + dy));
    }
    applySize(resize.node, widthPct, heightPx);
    placeBox();
    const label = resizeBox(true).querySelector('[data-size-label]');
    if (label) {
      const rect = resize.node.getBoundingClientRect();
      label.textContent = `${widthPct != null ? `${widthPct}%` : `${Math.round(rect.width)}px`} × ${Math.round(rect.height)}px`;
    }
    syncPreview(120);
  };

  const endResize = () => {
    if (!resize) {
      return;
    }
    win.removeEventListener('pointermove', onResizeMove, true);
    win.removeEventListener('pointerup', endResize, true);
    win.removeEventListener('pointercancel', endResize, true);
    body.removeAttribute('data-mcc-resizing');
    resize = null;
    placeBox();
    syncPreview(0);
  };

  doc.addEventListener(
    'pointerdown',
    (event) => {
      const target = eventEl(event);
      const grip = target && target.closest && target.closest('#mcc-resize-box > [data-grip]');
      if (!grip || !current || event.button !== 0) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const rect = current.getBoundingClientRect();
      editor.undoManager.add();
      if (grip.getAttribute('data-grip') === 'col') {
        const index = parseInt(grip.getAttribute('data-col-index'), 10);
        const cells = columnCellsOf(current);
        const left = cells[index];
        const right = cells[index + 1];
        const table = left && left.closest ? left.closest('table') : null;
        if (!left || !right || !table) {
          return;
        }
        resize = {
          dir: 'col',
          node: left,
          pair: right,
          x: event.clientX,
          y: event.clientY,
          leftPx: left.getBoundingClientRect().width,
          total: Math.max(1, table.getBoundingClientRect().width),
        };
        body.setAttribute('data-mcc-resizing', '1');
        win.addEventListener('pointermove', onResizeMove, true);
        win.addEventListener('pointerup', endResize, true);
        win.addEventListener('pointercancel', endResize, true);
        return;
      }
      resize = {
        node: current,
        dir: grip.getAttribute('data-grip'),
        x: event.clientX,
        y: event.clientY,
        w: rect.width,
        h: rect.height,
        slot: slotWidth(current),
        minH: naturalHeight(current),
      };
      body.setAttribute('data-mcc-resizing', '1');
      win.addEventListener('pointermove', onResizeMove, true);
      win.addEventListener('pointerup', endResize, true);
      win.addEventListener('pointercancel', endResize, true);
    },
    true
  );

  body.addEventListener('mouseover', (event) => {
    if (body.getAttribute('data-mcc-sorting') || resize) {
      return;
    }
    const target = eventEl(event);
    const skip = target && target.closest && target.closest('.mcc-card-tools, #mcc-resize-box');
    if (skip) {
      return;
    }
    setHover(hoverCardOf(target, body) || closestDraggable(target, body));
  });
  body.addEventListener('mouseleave', (event) => {
    const to = event.relatedTarget;
    if (resize || (to && to.closest && to.closest('#mcc-resize-box'))) {
      return;
    }
    setHover(null);
  });
  win.addEventListener('scroll', placeBox, true);
  win.addEventListener('resize', placeBox);
  editor.on('NodeChange keyup', placeBox);
};

const cleanupEmptyWrapRow = (row, wrap) => {
  if (!row || !row.parentNode || (wrap && !wrap.contains(row))) {
    return;
  }
  const isEmpty = (node) =>
    !node.querySelector(
      '.mcc-layout-card, [data-mcc-pay-now-wrap], [data-mcc-block], [data-mcc-total-due], img, p, table'
    ) && !String(node.textContent || '').replace(/[\s\u00a0]+/g, '');
  const table = row.parentNode.closest ? row.parentNode.closest('table') : null;
  if (table === wrap && row.cells && row.cells.length > 1) {
    Array.prototype.slice.call(row.cells).forEach((cell) => {
      if (row.cells.length > 1 && isEmpty(cell)) {
        row.removeChild(cell);
      }
    });
    if (row.cells.length === 1) {
      row.cells[0].removeAttribute('width');
      row.cells[0].style.removeProperty('width');
    }
  }
  if (isEmpty(row)) {
    row.parentNode.removeChild(row);
  }
};

const attachSmoothCanvasDrag = (editor) => {
  const body = editor.getBody();
  const doc = editor.getDoc();
  if (!body || !doc || body.getAttribute('data-mcc-smooth-drag') === '1') {
    return;
  }
  body.setAttribute('data-mcc-smooth-drag', '1');
  let drag = null;

  const clearDropLine = () => {
    const line = doc.getElementById('mcc-drop-line');
    if (line && line.parentNode) {
      line.parentNode.removeChild(line);
    }
  };

  const showDropLine = (rect, after, verticalBar) => {
    let line = doc.getElementById('mcc-drop-line');
    if (!line) {
      line = doc.createElement('div');
      line.id = 'mcc-drop-line';
      line.setAttribute('data-mce-bogus', 'all');
      doc.body.appendChild(line);
    }
    if (verticalBar) {
      line.style.width = '3px';
      line.style.height = `${Math.max(rect.height, 24)}px`;
      line.style.left = `${after ? rect.right - 1 : rect.left - 1}px`;
      line.style.top = `${rect.top}px`;
    } else {
      line.style.width = `${Math.max(rect.width, 40)}px`;
      line.style.height = '3px';
      line.style.left = `${rect.left}px`;
      line.style.top = `${after ? rect.bottom - 1 : rect.top - 1}px`;
    }
  };

  const moveIntoRowSide = (node, row, side) => {
    let cell = node;
    if (node.nodeName !== 'TD' && node.nodeName !== 'TH') {
      cell = doc.createElement('td');
      cell.setAttribute('valign', 'top');
      cell.className = 'stack';
      cell.appendChild(node);
    }
    if (side === 'left' && row.cells && row.cells[0]) {
      row.insertBefore(cell, row.cells[0]);
    } else {
      row.appendChild(cell);
    }
    Array.prototype.forEach.call(row.cells || [], (td) => {
      setWrapCellWidth(td, `${Math.floor(100 / row.cells.length)}%`);
    });
  };

  const applyDrop = (node, x, y) => {
    const wrap = wrapTableOf(body);
    const host = wrapHost(wrap);
    if (!node || !wrap || !host) {
      return;
    }
    if (node.nodeName === 'TD' || node.nodeName === 'TR') {
      return;
    }
    const fromRow = wrapRowOf(node, wrap);
    const column = columnDropTarget(doc, x, y, node);
    if (column) {
      column.appendChild(node);
      if (fromRow && !fromRow.contains(node)) {
        cleanupEmptyWrapRow(fromRow, wrap);
      }
      return;
    }
    const hit = nearestWrapRow(wrap, x, y, node);
    if (!hit) {
      if (!wrap.contains(node)) {
        host.appendChild(makeWrapRow(doc, node, '12px 24px'));
      }
      return;
    }
    const side = sideForRect(hit.rect, x, y);
    if (!isFooterRow(hit.el) && (side === 'left' || side === 'right')) {
      moveIntoRowSide(node, hit.el, side);
      if (fromRow && fromRow !== hit.el && !fromRow.contains(node)) {
        cleanupEmptyWrapRow(fromRow, wrap);
      }
      return;
    }
    const siblings = fromRow
      ? fromRow.querySelectorAll(
          '.mcc-layout-card, [data-mcc-pay-now-wrap], [data-mcc-total-due]'
        ).length
      : 0;
    const bleed =
      node.getAttribute && node.getAttribute('data-mcc-bleed') === '1';
    const row =
      fromRow && fromRow.contains(node) && siblings <= 1
        ? fromRow
        : makeWrapRow(
            doc,
            node,
            isFooterLayoutNode(node) || bleed ? '0' : '12px 24px'
          );
    placeWrapRow(row, hit, side);
    if (fromRow && fromRow !== row && !fromRow.contains(node)) {
      cleanupEmptyWrapRow(fromRow, wrap);
    }
  };

  const stopDrag = (commit) => {
    if (!drag) {
      return;
    }
    const current = drag;
    drag = null;
    doc.removeEventListener('pointermove', current.move);
    doc.removeEventListener('pointerup', current.up);
    doc.removeEventListener('pointercancel', current.up);
    window.removeEventListener('pointerup', current.up);
    window.removeEventListener('pointercancel', current.up);
    if (commit) {
      applyDrop(current.node, current.lastX, current.lastY);
    }
    const snap = current.node.getBoundingClientRect();
    const finish = () => {
      clearDropLine();
      if (current.ghost && current.ghost.parentNode) {
        current.ghost.parentNode.removeChild(current.ghost);
      }
      current.node.removeAttribute('data-mcc-dragging');
      body.removeAttribute('data-mcc-sorting');
      if (commit) {
        editor.undoManager.add();
        editor.nodeChanged();
        editor.fire('change');
      }
    };
    if (current.ghost && snap && commit) {
      current.ghost.style.transition =
        'left 180ms cubic-bezier(0.2, 0.8, 0.2, 1), top 180ms cubic-bezier(0.2, 0.8, 0.2, 1), transform 180ms ease, box-shadow 180ms ease';
      current.ghost.style.left = `${snap.left}px`;
      current.ghost.style.top = `${snap.top}px`;
      current.ghost.style.transform = 'scale(1)';
      current.ghost.style.boxShadow = '0 2px 8px rgba(15, 23, 42, 0.08)';
      window.setTimeout(finish, 190);
      return;
    }
    finish();
  };

  const onDown = (event) => {
    if (event.button != null && event.button !== 0) {
      return;
    }
    const target = eventEl(event);
    if (!target || !target.closest) {
      return;
    }
    if (
      target.closest(
        '.mcc-card-delete, #mcc-resize-box, [data-mcc-pay-note], img[data-icon="rx"]'
      )
    ) {
      return;
    }
    const handle = target.closest('.mcc-card-handle');
    const payButton = target.closest('a[data-mcc-pay-now]');
    const img =
      target.nodeName === 'IMG' &&
      !isPoweredByLock(target) &&
      target.getAttribute('data-logo') !== 'pharmacy' &&
      target.getAttribute('data-logo') !== 'account'
        ? target
        : null;
    let node = null;
    if (handle) {
      node = closestDraggable(handle, body);
    } else if (payButton) {
      node =
        target.closest('[data-mcc-pay-now-wrap]') ||
        closestDraggable(payButton, body);
    } else if (img) {
      node = img;
    }
    if (
      !node ||
      node.nodeName === 'TD' ||
      node.nodeName === 'TR' ||
      isFooterLayoutNode(node) ||
      isLockedLayoutCard(node)
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const rect = node.getBoundingClientRect();
    const ghost = node.cloneNode(true);
    Array.prototype.forEach.call(
      ghost.querySelectorAll('.mcc-card-tools, #mcc-drop-line'),
      (el) => {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      }
    );
    ghost.setAttribute('data-mce-bogus', 'all');
    ghost.setAttribute('data-mcc-ghost', '1');
    ghost.style.position = 'fixed';
    ghost.style.left = `${rect.left}px`;
    ghost.style.top = `${rect.top}px`;
    ghost.style.width = `${rect.width}px`;
    ghost.style.margin = '0';
    ghost.style.zIndex = '10060';
    ghost.style.pointerEvents = 'none';
    ghost.style.boxShadow = '0 14px 32px rgba(15,23,42,0.22)';
    ghost.style.opacity = '1';
    ghost.style.boxSizing = 'border-box';
    doc.body.appendChild(ghost);
    node.setAttribute('data-mcc-dragging', '1');
    body.setAttribute('data-mcc-sorting', '1');
    const start = framePoint(editor, event) || {
      x: event.clientX,
      y: event.clientY,
    };
    drag = {
      node,
      ghost,
      offsetX: start.x - rect.left,
      offsetY: start.y - rect.top,
      lastX: start.x,
      lastY: start.y,
    };
    const placeGhost = (point) => {
      drag.ghost.style.left = `${point.x - drag.offsetX}px`;
      drag.ghost.style.top = `${point.y - drag.offsetY}px`;
    };
    drag.move = (moveEvent) => {
      moveEvent.preventDefault();
      if (!drag || !drag.ghost) {
        return;
      }
      const point = framePoint(editor, moveEvent) || {
        x: moveEvent.clientX,
        y: moveEvent.clientY,
      };
      drag.lastX = point.x;
      drag.lastY = point.y;
      placeGhost(point);
      const column = columnDropTarget(doc, point.x, point.y, node);
      if (column) {
        showDropLine(column.getBoundingClientRect(), true, false);
        return;
      }
      const hit = nearestWrapRow(wrapTableOf(body), point.x, point.y, node);
      if (!hit) {
        clearDropLine();
        return;
      }
      const side = sideForRect(hit.rect, point.x, point.y);
      showDropLine(
        hit.rect,
        side === 'after' || side === 'right',
        side === 'left' || side === 'right'
      );
    };
    drag.up = (upEvent) => {
      if (!drag) {
        return;
      }
      const point = framePoint(editor, upEvent) || {
        x: drag.lastX,
        y: drag.lastY,
      };
      const moved =
        Math.abs(point.x - start.x) > 4 || Math.abs(point.y - start.y) > 4;
      if (moved) {
        drag.lastX = point.x;
        drag.lastY = point.y;
      }
      stopDrag(moved);
    };
    doc.addEventListener('pointermove', drag.move);
    doc.addEventListener('pointerup', drag.up);
    doc.addEventListener('pointercancel', drag.up);
    window.addEventListener('pointerup', drag.up);
    window.addEventListener('pointercancel', drag.up);
    const captureTarget = handle || node;
    if (captureTarget.setPointerCapture && event.pointerId != null) {
      try {
        captureTarget.setPointerCapture(event.pointerId);
      } catch (_err) {
        // Pointer capture is optional. The document listeners still track the drag.
      }
    }
  };

  body.addEventListener('pointerdown', onDown, true);
};

const nodeFromDrop = (editor, dropEvent) => {
  const doc = editor.getDoc();
  if (!doc || !dropEvent) {
    return editor.selection && editor.selection.getNode();
  }
  let node = dropEvent.target;
  if (doc.caretRangeFromPoint && typeof dropEvent.clientX === 'number') {
    try {
      const range = doc.caretRangeFromPoint(
        dropEvent.clientX,
        dropEvent.clientY
      );
      if (range && range.startContainer) {
        node = range.startContainer;
      }
    } catch (_err) {
      // keep event.target
    }
  }
  if (node && node.nodeType === 3) {
    node = node.parentNode;
  }
  return node;
};

const insertLayoutBlock = (editor, html, dropEvent) => {
  const body = editor.getBody();
  const doc = editor.getDoc();
  if (!body || !doc) {
    editor.insertContent(html);
    return;
  }
  const holder = doc.createElement('div');
  holder.innerHTML = html;
  const nodes = Array.prototype.slice
    .call(holder.childNodes)
    .filter((node) => {
      if (node.nodeType === 1) {
        return true;
      }
      return node.nodeType === 3 && String(node.textContent || '').trim();
    });
  if (!nodes.length) {
    editor.insertContent(html);
    return;
  }
  const point = framePoint(editor, dropEvent);
  const dropNode = point
    ? (doc.elementFromPoint && doc.elementFromPoint(point.x, point.y)) ||
      nodeFromDrop(editor, dropEvent)
    : nodeFromDrop(editor, dropEvent);
  const anchor =
    closestLayoutBlock(editor, dropNode) ||
    closestLayoutBlock(editor, editor.selection.getNode());
  const wrap = wrapTableOf(body);
  const isFooter = nodes.some((node) => node.nodeType === 1 && isFooterLayoutNode(node));
  editor.undoManager.transact(() => {
    if (wrap) {
      const host = wrapHost(wrap);
      const column =
        point && !isFooter ? columnDropTarget(doc, point.x, point.y, null) : null;
      if (column) {
        nodes.forEach((node) => {
          column.appendChild(node);
        });
        return;
      }
      const bleed = nodes.some(
        (node) =>
          node.nodeType === 1 && node.getAttribute('data-mcc-bleed') === '1'
      );
      const row = makeWrapRow(doc, nodes, isFooter || bleed ? '0' : '12px 24px');
      const hit = point ? nearestWrapRow(wrap, point.x, point.y, null) : null;
      const wrapRow = wrapRowOf(anchor, wrap);
      if (hit && hit.el && hit.el.parentNode) {
        const side = sideForRect(hit.rect, point.x, point.y);
        if (!isFooter && !isFooterRow(hit.el) && (side === 'left' || side === 'right')) {
          const cell = row.cells && row.cells[0];
          if (cell) {
            if (side === 'left' && hit.el.cells && hit.el.cells[0]) {
              hit.el.insertBefore(cell, hit.el.cells[0]);
            } else {
              hit.el.appendChild(cell);
            }
            Array.prototype.forEach.call(hit.el.cells || [], (td) => {
              setWrapCellWidth(td, `${Math.floor(100 / hit.el.cells.length)}%`);
            });
            return;
          }
        }
        placeWrapRow(row, hit, side);
        return;
      }
      if (wrapRow && wrapRow.parentNode) {
        const rect = wrapRow.getBoundingClientRect();
        const insertAfter =
          !point || point.y > rect.top + rect.height / 2;
        placeWrapRow(row, { el: wrapRow, rect }, insertAfter ? 'after' : 'before');
        return;
      }
      host.appendChild(row);
      return;
    }
    const fragment = doc.createDocumentFragment();
    nodes.forEach((node) => {
      fragment.appendChild(node);
    });
    const anchorRect = anchor && anchor.getBoundingClientRect ? anchor.getBoundingClientRect() : null;
    const insertAfter =
      !anchorRect || !point || point.y > anchorRect.top + anchorRect.height / 2;
    if (anchor && anchor.parentNode) {
      if (insertAfter) {
        if (anchor.nextSibling) {
          anchor.parentNode.insertBefore(fragment, anchor.nextSibling);
        } else {
          anchor.parentNode.appendChild(fragment);
        }
      } else {
        anchor.parentNode.insertBefore(fragment, anchor);
      }
    } else {
      body.appendChild(fragment);
    }
  });
  editor.nodeChanged();
  editor.fire('SetContent');
  editor.fire('change');
};

class HtmlEditor extends Component {
  constructor(props) {
    super(props);
    editorSeq += 1;
    this.editorId = `feature-html-editor-${editorSeq}`;
    this.editor = null;
    this.unmounted = false;
    this.ready = false;
    this.suppressChange = false;
    this.lastEmitted = props.value || '';
    this.emitTimer = null;
    this.canvasTimer = null;
    this.canvasUnlock = null;
    this.initGen = 0;
    this.pharmacyLogoPick = null;
    this.scheduleCanvasRefresh = null;
    this.state = { loadError: '' };
  }

  componentDidMount() {
    this.initEditor();
  }

  componentDidUpdate(prevProps) {
    if (this.unmounted) {
      return;
    }
    const presetChanged =
      prevProps.preset !== this.props.preset ||
      prevProps.enableImages !== this.props.enableImages ||
      prevProps.compact !== this.props.compact;
    if (presetChanged) {
      this.destroyEditor({ silent: true });
      this.initEditor();
      return;
    }
    if (!this.editor || !this.ready) {
      return;
    }
    try {
      if (prevProps.disabled !== this.props.disabled) {
        this.editor.mode.set(this.props.disabled ? 'readonly' : 'design');
      }
      if (prevProps.value !== this.props.value) {
        const nextValue = this.props.value || '';
        const current = this.editor.getContent();
        const logoUrl = String(this.props.logoUrl || '').trim();
        const logoNeedsPaint =
          !!logoUrl &&
          nextValue.indexOf(logoUrl) !== -1 &&
          current.indexOf(logoUrl) === -1;
        if (
          current !== nextValue &&
          this.lastEmitted !== nextValue &&
          (!this.editor.hasFocus() || logoNeedsPaint)
        ) {
          this.suppressChange = true;
          this.lastEmitted = nextValue;
          this.editor.setContent(nextValue);
          this.releaseSuppressChange();
          if (typeof this.scheduleCanvasRefresh === 'function') {
            this.scheduleCanvasRefresh(true);
          }
        }
      }
      this.paintAccountLogo();
    } catch (_err) {
      this.ready = false;
    }
  }

  paintAccountLogo = () => {
    const logoUrl = String(this.props.logoUrl || '').trim();
    if (!logoUrl || !this.editor || !this.ready || this.unmounted) {
      return;
    }
    if (/ilocal-logo-primary/i.test(logoUrl)) {
      return;
    }
    const body = this.editor.getBody();
    if (!body) {
      return;
    }
    const img =
      body.querySelector(
        'img[data-token="Account Logo"], img[data-logo="account"]'
      ) || body.querySelector('[data-card="header"] img, [data-brand="header"] img');
    if (!img) {
      return;
    }
    const shown = img.getAttribute('src') || '';
    const custom =
      !!shown && !/ilocal-logo-primary/i.test(shown) && shown !== logoUrl;
    const nonEditable =
      img.classList.contains('mceNonEditable') ||
      img.getAttribute('data-locked') === 'true';
    const tokenized =
      img.getAttribute('data-token') === 'Account Logo' ||
      img.getAttribute('data-logo') === 'account';
    if (custom && !nonEditable && !tokenized) {
      return;
    }
    if (shown === logoUrl && !nonEditable) {
      return;
    }
    const next = injectAccountLogo(
      this.editor.getContent() || this.props.value || '',
      logoUrl
    );
    if (!next || (!custom && next.indexOf(logoUrl) === -1)) {
      return;
    }
    this.suppressChange = true;
    this.lastEmitted = next;
    try {
      this.editor.setContent(next);
    } catch (_err) {
      this.suppressChange = false;
      return;
    }
    this.releaseSuppressChange();
    if (this.props.onChange && next !== this.props.value) {
      this.props.onChange(next);
    }
  };

  componentWillUnmount() {
    this.unmounted = true;
    this.ready = false;
    this.clearEditorTimers();
    this.destroyEditor();
  }

  destroyEditor = (options = {}) => {
    try {
      if (
        !options.silent &&
        this.editor &&
        this.ready &&
        this.props.onChange
      ) {
        this.props.onChange(this.editor.getContent());
      }
    } catch (_err) {
      // ignore
    }
    this.ready = false;
    this.clearEditorTimers();
    try {
      if (this.editor) {
        this.editor.off(
          'change keyup undo redo paste SetContent input ExecCommand'
        );
        this.editor.remove();
      } else if (window.tinymce && this.editorId) {
        window.tinymce.remove(`#${this.editorId}`);
      }
    } catch (_err) {
      // TinyMCE can throw if the instance was already torn down during a tab switch.
    }
    this.editor = null;
  };

  insertToken = (name, locked = false) => {
    const html = `<span class="mceNonEditable" data-locked="${
      locked ? 'true' : 'false'
    }" data-token="${name}">{{${name}}}</span>`;
    return this.insertHtml(html);
  };

  clearEditorTimers = () => {
    if (this.emitTimer) {
      window.clearTimeout(this.emitTimer);
      this.emitTimer = null;
    }
    if (this.canvasTimer) {
      window.clearTimeout(this.canvasTimer);
      this.canvasTimer = null;
    }
    if (this.canvasUnlock) {
      window.cancelAnimationFrame(this.canvasUnlock);
      this.canvasUnlock = null;
    }
  };

  releaseSuppressChange = () => {
    if (this.canvasUnlock) {
      window.cancelAnimationFrame(this.canvasUnlock);
    }
    this.canvasUnlock = window.requestAnimationFrame(() => {
      this.canvasUnlock = null;
      this.suppressChange = false;
      if (this.pendingEmit) {
        this.pendingEmit = false;
        this.emitCurrentContent();
      }
    });
  };

  emitCurrentContent = () => {
    if (this.unmounted || !this.ready || !this.editor) {
      return;
    }
    if (this.suppressChange) {
      this.pendingEmit = true;
      return;
    }
    if (!this.props.onChange) {
      return;
    }
    try {
      const content = this.editor.getContent();
      if (content === this.lastEmitted) {
        return;
      }
      this.lastEmitted = content;
      this.props.onChange(content);
    } catch (_err) {
      // ignore
    }
  };

  // Save and Publish read React state. A picture can still be a blob in
  // TinyMCE until uploadImages runs, so read the editor itself first.
  flushContent = async () => {
    if (this.unmounted || !this.ready || !this.editor) {
      return null;
    }
    if (this.emitTimer) {
      window.clearTimeout(this.emitTimer);
      this.emitTimer = null;
    }
    try {
      if (typeof this.editor.uploadImages === 'function') {
        await this.editor.uploadImages();
      }
    } catch (error) {
      const message =
        (error && error.message) || 'Unable to upload that image.';
      throw new Error(message);
    }
    let content = '';
    try {
      content = this.editor.getContent();
    } catch (_err) {
      return null;
    }
    if (this.props.onChange && content !== this.lastEmitted) {
      this.lastEmitted = content;
      this.props.onChange(content);
    } else {
      this.lastEmitted = content;
    }
    return content;
  };

  insertHtml = (html, options = {}) => {
    const snippet = String(html || '');
    if (!snippet) {
      return false;
    }
    try {
      if (this.editor && this.ready) {
        if (options.block) {
          insertLayoutBlock(this.editor, snippet, options.dropEvent);
        } else {
          this.editor.insertContent(snippet);
        }
        this.emitCurrentContent();
        if (options.block && typeof this.scheduleCanvasRefresh === 'function') {
          this.scheduleCanvasRefresh(true);
        }
        return true;
      }
    } catch (_err) {
      // fall through
    }
    if (this.props.onChange) {
      this.props.onChange(`${this.props.value || ''}${snippet}`);
    }
    return false;
  };

  insertPayNow = (html) => {
    const snippet = String(html || '');
    if (!snippet) {
      return false;
    }
    const editor = this.editor;
    if (!editor || !this.ready) {
      return false;
    }
    const body = editor.getBody();
    const doc = editor.getDoc();
    if (!body || !doc) {
      return false;
    }
    try {
      editor.undoManager.transact(() => {
        Array.prototype.slice
          .call(body.querySelectorAll('[data-card="payment"], [data-card="cta"]'))
          .forEach((el) => {
            if (!el || !el.parentNode) {
              return;
            }
            const wrap = wrapTableOf(body);
            const row = wrapRowOf(el, wrap);
            el.parentNode.removeChild(el);
            if (
              row &&
              row.parentNode &&
              !row.querySelector('[data-card], .mcc-layout-card, [data-mcc-pay-now], [data-mcc-total-due]')
            ) {
              row.parentNode.removeChild(row);
            }
          });
        Array.prototype.slice
          .call(
            body.querySelectorAll(
              '[data-mcc-pay-now-wrap], [data-mcc-pay-now], [data-mcc-pay-note], [data-token="Link"]'
            )
          )
          .forEach((el) => {
            if (el && el.parentNode) {
              el.parentNode.removeChild(el);
            }
          });
        const holder = doc.createElement('div');
        holder.innerHTML = snippet;
        const nodes = Array.prototype.slice.call(holder.childNodes).filter((node) => {
          if (node.nodeType === 1) {
            return true;
          }
          return node.nodeType === 3 && String(node.textContent || '').trim();
        });
        const due = body.querySelector('[data-mcc-total-due]');
        const heading = Array.prototype.slice
          .call(body.querySelectorAll('p, td, div'))
          .filter((el) => /Payment Summary/i.test(el.textContent || ''))[0];
        const host = due
          ? due.parentNode
          : heading
          ? heading.parentNode
          : null;
        const after = due || heading;
        if (host && after) {
          let ref = after.nextSibling;
          nodes.forEach((node) => {
            host.insertBefore(node, ref);
          });
        } else {
          insertLayoutBlock(editor, snippet);
        }
      });
      this.emitCurrentContent();
      return true;
    } catch (_err) {
      return false;
    }
  };

  openPharmacyLogoDialog = () => {
    const editor = this.editor;
    if (!editor || !this.ready || this.unmounted) {
      return false;
    }
    const body = editor.getBody();
    const slot = body && body.querySelector('[data-logo-slot="pharmacy"]');
    if (!slot) {
      return false;
    }
    const existingImg = slot.querySelector('img');
    let target = existingImg;
    if (!target) {
      target = editor.dom.create('img', {
        'data-logo': 'pharmacy',
        'data-card': 'brand-logo',
        src:
          'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
        alt: 'Pharmacy logo',
        width: '110',
        height: '36',
        style: PHARMACY_LOGO_STYLE,
      });
      slot.innerHTML = '';
      slot.appendChild(target);
    }
    this.pharmacyLogoPick = {
      slot,
      srcs: collectImageSrcs(body),
      hadSrc: !!(
        existingImg && isUsableImageSrc(existingImg.getAttribute('src'))
      ),
    };
    try {
      editor.selection.select(target);
      editor.focus();
      editor.execCommand('mceImage');
      focusImageUploadTab();
      return true;
    } catch (_err) {
      this.pharmacyLogoPick = null;
      return false;
    }
  };

  finishPharmacyLogoPick = () => {
    const editor = this.editor;
    const pick = this.pharmacyLogoPick;
    if (!editor || !pick) {
      return;
    }
    this.pharmacyLogoPick = null;
    applyPharmacyLogoPick(editor, pick);
    this.emitCurrentContent();
  };

  openSectionIconDialog = (img) => {
    const editor = this.editor;
    if (!editor || !this.ready || !img) {
      return false;
    }
    const fallback = img.getAttribute('data-icon-default') || img.getAttribute('src') || '';
    this.sectionIconPick = { img, defaultSrc: fallback };
    try {
      editor.selection.select(img);
      editor.focus();
      editor.execCommand('mceImage');
      focusImageUploadTab();
      return true;
    } catch (_err) {
      this.sectionIconPick = null;
      return false;
    }
  };

  finishSectionIconPick = () => {
    const pick = this.sectionIconPick;
    if (!pick) {
      return;
    }
    this.sectionIconPick = null;
    const editor = this.editor;
    const body = editor && editor.getBody();
    const selected = editor && editor.selection && editor.selection.getNode();
    let img = pick.img && pick.img.isConnected ? pick.img : null;
    if (!img && selected && selected.nodeName === 'IMG') {
      img = selected;
    }
    if (!img && body) {
      img = body.querySelector('[data-mcc-icon-row="rx"] img, img[data-icon="rx"]');
    }
    if (!img) {
      return;
    }
    const src = img.getAttribute('src') || '';
    const next = isUsableImageSrc(src) ? src : pick.defaultSrc;
    img.setAttribute('src', next);
    img.setAttribute('data-mce-src', next);
    img.setAttribute('data-icon', 'rx');
    if (pick.defaultSrc) {
      img.setAttribute('data-icon-default', pick.defaultSrc);
    }
    const style = img.getAttribute('style') || '';
    if (style) {
      img.setAttribute('data-mce-style', style.replace(/\s*!important/gi, ''));
    }
    this.emitCurrentContent();
  };

  initEditor = () => {
    const self = this;
    this.initGen += 1;
    const gen = this.initGen;
    loadTinyMce()
      .then((tinymce) => {
        if (self.unmounted || gen !== self.initGen || !tinymce) {
          return;
        }
        const compact = Boolean(self.props.compact);
        const preset = self.props.preset;
        const enableImages =
          self.props.enableImages !== false && !compact && preset !== 'sms';
        const uploader = self.props.uploadImage || uploadFeatureCatalogImage;
        const height = self.props.height
          ? self.props.height
          : self.props.fillHeight
          ? 'calc(100vh - 220px)'
          : compact
          ? 220
          : 380;
        const smsToolbar =
          'undo redo | blocks | bold italic | inserttoken | removeformat';
        const emailToolbar =
          'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright alignjustify | place | bullist numlist outdent indent | image media | hr | inserttoken | insertcard | removeformat | code';
        const config = {
          selector: `#${self.editorId}`,
          base_url: 'https://cdn.jsdelivr.net/npm/tinymce@6.8.4',
          suffix: '.min',
          promotion: false,
          branding: false,
          menubar: compact
            ? false
            : preset === 'sms'
            ? false
            : preset === 'email'
            ? 'insert format table tools help'
            : 'file edit view insert format tools table help',
          plugins: tinymcePluginsFor(preset, compact),
          toolbar: preset === 'sms'
            ? smsToolbar
            : preset === 'email'
            ? emailToolbar
            : compact
            ? 'undo redo | blocks | bold italic underline | bullist numlist | link | removeformat'
            : 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | forecolor backcolor | removeformat | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | blockquote hr | link image media table | charmap emoticons | ltr rtl | searchreplace visualblocks code fullscreen preview | insertdatetime pagebreak | help',
          toolbar_mode: 'wrap',
          object_resizing: preset === 'email' ? 'img' : false,
          table_toolbar: preset === 'email' ? '' : undefined,
          table_sizing_mode: preset === 'email' ? 'relative' : undefined,
          table_use_colgroups: preset === 'email' ? false : undefined,
          table_resize_bars: preset === 'email' ? false : undefined,
          table_column_resizing:
            preset === 'email' ? 'preservetable' : undefined,
          valid_children:
            preset === 'email'
              ? '+body[style|table|div],+div[table|div|p|a|img|span|button],+td[table|div|p|a|img|span|strong|em|button],+th[table|div|p|a|img|span|button],+span[button]'
              : preset === 'sms'
              ? '+body[div|p|img|#text],+p[#text|img|div|span|br|strong|em|b|i],+div[#text|img|div|span|p|br|strong|em|b|i]'
              : undefined,
          verify_html: preset === 'sms' ? false : undefined,
          invalid_elements: preset === 'email' ? 'colgroup,col' : undefined,
          content_css: preset === 'email' ? false : undefined,
          forced_root_block: preset === 'email' ? false : undefined,
          width: '100%',
          height: height,
          min_height: self.props.fillHeight ? 320 : compact ? 160 : 280,
          resize: true,
          statusbar: !compact,
          relative_urls: false,
          convert_urls: false,
          remove_script_host: false,
          noneditable_class: 'mceNonEditable',
          valid_styles:
            preset === 'email'
              ? {
                  '*': EMAIL_VALID_STYLES,
                }
              : undefined,
          extended_valid_elements:
            preset === 'email'
              ? EMAIL_EXTENDED_ELEMENTS
              : preset === 'sms'
              ? 'img[src|alt|width|height|style|class|data-token|data-logo|data-qr-src|data-brand],div[style|class|data-qr-mark|data-token|data-region|data-max]'
              : undefined,
          content_style:
            preset === 'email'
              ? EMAIL_EDITOR_CONTENT_CSS
              : preset === 'sms'
              ? SMS_EDITOR_CONTENT_CSS
              : DEFAULT_EDITOR_CONTENT_CSS,
          setup: (editor) => {
            if (self.unmounted || gen !== self.initGen) {
              try {
                editor.remove();
              } catch (_err) {
                // ignore
              }
              return;
            }
            self.editor = editor;
            const blockLockedDelete = (event) => {
              const node = editor.selection && editor.selection.getNode();
              if (nodeTouchesPoweredBy(node, editor.getBody())) {
                event.preventDefault();
                if (event.stopImmediatePropagation) {
                  event.stopImmediatePropagation();
                }
                return;
              }
              try {
                const range = editor.selection.getRng();
                if (!range || !range.cloneContents) {
                  return;
                }
                const fragment = range.cloneContents();
                if (
                  fragment.querySelector &&
                  fragment.querySelector('[data-locked-widget="powered_by"]')
                ) {
                  event.preventDefault();
                  if (event.stopImmediatePropagation) {
                    event.stopImmediatePropagation();
                  }
                }
              } catch (_err) {
                // ignore
              }
            };
            editor.on('keydown', (event) => {
              if (event.keyCode !== 8 && event.keyCode !== 46) {
                return;
              }
              blockLockedDelete(event);
            });
            editor.on('cut', blockLockedDelete);
            editor.on('contextmenu', (event) => {
              const target = event.target;
              if (
                target &&
                target.closest &&
                target.closest(
                  '[data-locked-widget="powered_by"], [data-card="branding"]'
                )
              ) {
                event.preventDefault();
                if (event.stopImmediatePropagation) {
                  event.stopImmediatePropagation();
                }
              }
            });
            editor.on('BeforeExecCommand', (event) => {
              const command = String((event && event.command) || '');
              if (!/delete|cut/i.test(command)) {
                return;
              }
              blockLockedDelete(event);
            });
            const tokens = Array.isArray(self.props.tokens) ? self.props.tokens : [];
            if (preset === 'sms' || preset === 'email') {
              editor.ui.registry.addMenuButton('inserttoken', {
                text: 'Token',
                tooltip:
                  'Insert a merge token. Tokens are replaced with real patient and store data when the message is sent.',
                fetch: (callback) => {
                  callback(
                    tokens.map((token) => ({
                      type: 'menuitem',
                      text: token.hint
                        ? `${token.name} — ${token.hint}`
                        : token.name,
                      onAction: () => {
                        if (typeof self.props.onSelectToken === 'function') {
                          const handled = self.props.onSelectToken(token);
                          if (handled !== false) {
                            return;
                          }
                        }
                        editor.insertContent(
                          `<span class="mceNonEditable" data-locked="${
                            token.required ? 'true' : 'false'
                          }" data-token="${token.name}">{{${token.name}}}</span>`
                        );
                      },
                    }))
                  );
                },
              });
            }
            if (preset === 'email') {
              const listCards = () =>
                Array.isArray(self.props.layoutCards)
                  ? self.props.layoutCards
                  : [];
              editor.ui.registry.addMenuButton('insertcard', {
                text: 'Card',
                tooltip: 'Insert a layout card into the email.',
                fetch: (callback) => {
                  callback(
                    listCards().map((card) => ({
                      type: 'menuitem',
                      text: card.label,
                      onAction: () => {
                        if (typeof self.props.onInsertCard === 'function') {
                          self.props.onInsertCard(card.id);
                          return;
                        }
                        editor.insertContent(card.html || '');
                      },
                    }))
                  );
                },
              });
              const runArrange = (dir) => {
                editor.undoManager.transact(() => {
                  moveSection(editor, editor.selection.getNode(), dir);
                });
                editor.nodeChanged();
                editor.fire('change');
              };
              const runPlace = (where) => {
                editor.undoManager.transact(() => {
                  applyPlace(editor, where);
                });
                editor.nodeChanged();
                editor.fire('change');
              };
              editor.ui.registry.addMenuButton('arrange', {
                text: 'Move',
                tooltip:
                  'Move the selected section up, down, left, or right. Left/right places two sections side by side.',
                fetch: (callback) => {
                  callback([
                    {
                      type: 'menuitem',
                      text: 'Move up',
                      onAction: () => runArrange('up'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Move down',
                      onAction: () => runArrange('down'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Move left',
                      onAction: () => runArrange('left'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Move right',
                      onAction: () => runArrange('right'),
                    },
                  ]);
                },
              });
              editor.ui.registry.addMenuButton('place', {
                text: 'Place',
                tooltip:
                  'Align text, images, or sections left, center, right, top, or bottom',
                fetch: (callback) => {
                  callback([
                    {
                      type: 'menuitem',
                      text: 'Left',
                      onAction: () => runPlace('left'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Center',
                      onAction: () => runPlace('center'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Right',
                      onAction: () => runPlace('right'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Top',
                      onAction: () => runPlace('top'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Middle',
                      onAction: () => runPlace('middle'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Bottom',
                      onAction: () => runPlace('bottom'),
                    },
                  ]);
                },
              });
              editor.on('BeforeExecCommand', (event) => {
                if (event && event.command === 'mceFullScreen') {
                  event.preventDefault();
                  if (event.stopImmediatePropagation) {
                    event.stopImmediatePropagation();
                  }
                }
              });
              editor.on('ExecCommand', (event) => {
                const command = String((event && event.command) || '');
                if (preset === 'email' && /delete|cut/i.test(command)) {
                  window.setTimeout(() => {
                    if (self.unmounted || !self.editor) {
                      return;
                    }
                    const body = self.editor.getBody();
                    if (
                      body &&
                      !body.querySelector('[data-locked-widget="powered_by"]')
                    ) {
                      self.suppressChange = true;
                      restorePoweredBy(self.editor);
                      if (typeof self.scheduleCanvasRefresh === 'function') {
                        self.scheduleCanvasRefresh(true);
                      }
                      self.releaseSuppressChange();
                    }
                  }, 0);
                }
                const map = {
                  JustifyLeft: 'left',
                  JustifyCenter: 'center',
                  JustifyRight: 'right',
                };
                const where = event && map[event.command];
                if (!where) {
                  return;
                }
                const node = editor.selection && editor.selection.getNode();
                if (node && node.nodeName === 'IMG') {
                  applyImageHAlign(node, where);
                }
              });
              const isLayoutCard = (cardId) =>
                listCards().some((card) => card.id === cardId);
              const parseCardId = (raw) => {
                const value = String(raw || '').trim();
                if (value.indexOf('mcc-card:') === 0) {
                  return value.slice('mcc-card:'.length);
                }
                return value;
              };
              const isMoveDrag = (event) => {
                const body = editor.getBody();
                const transfer = event && event.dataTransfer;
                const types =
                  transfer && transfer.types ? Array.from(transfer.types) : [];
                return (
                  types.indexOf('text/mcc-move') !== -1 ||
                  !!(body && body.querySelector('[data-mcc-dragging="1"]'))
                );
              };
              editor.on('dragover', (event) => {
                const transfer = event.dataTransfer;
                const types = transfer && transfer.types
                  ? Array.from(transfer.types)
                  : [];
                if (isMoveDrag(event)) {
                  event.preventDefault();
                  if (transfer) {
                    transfer.dropEffect = 'move';
                  }
                  return;
                }
                if (
                  types.indexOf('text/mcc-card') !== -1 ||
                  types.indexOf('text/plain') !== -1
                ) {
                  event.preventDefault();
                  if (transfer) {
                    transfer.dropEffect = 'copy';
                  }
                }
              });
              editor.on('drop', (event) => {
                const transfer = event.dataTransfer;
                if (!transfer) {
                  return;
                }
                if (event.mccCardHandled) {
                  return;
                }
                if (isMoveDrag(event)) {
                  event.preventDefault();
                  if (event.stopPropagation) {
                    event.stopPropagation();
                  }
                  return;
                }
                const cardId = parseCardId(
                  transfer.getData('text/mcc-card') ||
                    transfer.getData('text/plain')
                );
                if (!isLayoutCard(cardId)) {
                  return;
                }
                event.mccCardHandled = true;
                event.preventDefault();
                if (event.stopPropagation) {
                  event.stopPropagation();
                }
                if (event.stopImmediatePropagation) {
                  event.stopImmediatePropagation();
                }
                if (typeof self.props.onInsertCard === 'function') {
                  self.props.onInsertCard(cardId, event);
                  return;
                }
                const match = listCards().find((card) => card.id === cardId);
                insertLayoutBlock(
                  editor,
                  (match && match.html) || '',
                  event
                );
              });
              editor.on('init', () => {
                const bodyEl = editor.getBody();
                if (!bodyEl || bodyEl.getAttribute('data-mcc-drop') === '1') {
                  return;
                }
                bodyEl.setAttribute('data-mcc-drop', '1');
                attachSmoothCanvasDrag(editor);
                bodyEl.addEventListener(
                  'dragover',
                  (event) => {
                    const transfer = event.dataTransfer;
                    const types =
                      transfer && transfer.types
                        ? Array.from(transfer.types)
                        : [];
                    if (isMoveDrag(event)) {
                      event.preventDefault();
                      if (transfer) {
                        transfer.dropEffect = 'move';
                      }
                      return;
                    }
                    if (
                      types.indexOf('text/mcc-card') !== -1 ||
                      types.indexOf('text/plain') !== -1
                    ) {
                      event.preventDefault();
                      if (transfer) {
                        transfer.dropEffect = 'copy';
                      }
                    }
                  },
                  true
                );
                bodyEl.addEventListener(
                  'drop',
                  (event) => {
                    const transfer = event.dataTransfer;
                    if (!transfer) {
                      return;
                    }
                    if (event.mccCardHandled) {
                      return;
                    }
                    if (isMoveDrag(event)) {
                      event.preventDefault();
                      event.stopPropagation();
                      return;
                    }
                    const cardId = parseCardId(
                      transfer.getData('text/mcc-card') ||
                        transfer.getData('text/plain')
                    );
                    if (!isLayoutCard(cardId)) {
                      return;
                    }
                    event.mccCardHandled = true;
                    event.preventDefault();
                    event.stopPropagation();
                    if (event.stopImmediatePropagation) {
                      event.stopImmediatePropagation();
                    }
                    if (typeof self.props.onInsertCard === 'function') {
                      self.props.onInsertCard(cardId, event);
                    }
                  },
                  true
                );
              });
              const decorateDeletableSections = () => {
                const body = editor.getBody();
                const doc = editor.getDoc();
                if (!body || !doc) {
                  return;
                }
                const attachCardTools = (node, removeNode, withMove, withDelete = true) => {
                  if (
                    !node ||
                    Array.prototype.some.call(
                      node.querySelectorAll('.mcc-card-tools'),
                      (existing) => closestDraggable(existing, body) === node
                    )
                  ) {
                    return;
                  }
                  const tools = doc.createElement('span');
                  tools.className = 'mcc-card-tools';
                  tools.setAttribute('contenteditable', 'false');
                  tools.setAttribute('data-mce-bogus', 'all');
                  const bindClick = (btn, onClick) => {
                    editor.dom.bind(btn, 'mousedown', (event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    });
                    editor.dom.bind(btn, 'click', (event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onClick(event);
                    });
                  };
                  const mkBtn = (cls, title, label) => {
                    const btn = doc.createElement('button');
                    btn.type = 'button';
                    btn.className = cls;
                    btn.setAttribute('contenteditable', 'false');
                    btn.setAttribute('data-mce-bogus', 'all');
                    btn.setAttribute('title', title);
                    btn.appendChild(doc.createTextNode(label));
                    return btn;
                  };
                  if (withMove) {
                    const handle = mkBtn(
                      'mcc-card-handle',
                      'Drag to move this block',
                      '::'
                    );
                    tools.appendChild(handle);
                  }
                  if (withDelete) {
                    const del = mkBtn('mcc-card-delete', 'Remove section', '×');
                    bindClick(del, () => {
                      removeNode(node);
                      editor.undoManager.add();
                      editor.nodeChanged();
                      editor.fire('change');
                    });
                    tools.appendChild(del);
                  }
                  if (!tools.childNodes.length) {
                    return;
                  }
                  editor.dom.setStyle(node, 'position', 'relative');
                  if (
                    node.getAttribute('data-card') === 'footer' ||
                    node.nodeName === 'TABLE'
                  ) {
                    const row = node.querySelector('tr');
                    const cells = row ? row.querySelectorAll('td, th') : [];
                    const last = cells.length ? cells[cells.length - 1] : null;
                    if (last) {
                      editor.dom.setStyle(last, 'position', 'relative');
                      last.insertBefore(tools, last.firstChild);
                      return;
                    }
                  }
                  node.insertBefore(tools, node.firstChild);
                };
                const removeLayoutCard = (node) => {
                  const parent = node.parentNode;
                  if (!parent) {
                    return;
                  }
                  parent.removeChild(node);
                  if (
                    parent.className &&
                    String(parent.className).indexOf('mcc-email-outro') !== -1 &&
                    !parent.querySelector(
                      '[data-locked-widget="powered_by"], [data-card], table, img, p, div'
                    )
                  ) {
                    if (parent.parentNode) {
                      parent.parentNode.removeChild(parent);
                    }
                  }
                };
                const stackCellsOf = (table) => {
                  const row = table && table.querySelector && table.querySelector('tr');
                  if (!row) {
                    return [];
                  }
                  return Array.prototype.filter.call(row.cells || [], (cell) =>
                    /(^|\s)stack(\s|$)/.test(String(cell.className || ''))
                  );
                };
                const removeStackCell = (cell) => {
                  const row = cell && cell.parentNode;
                  if (!row) {
                    return;
                  }
                  row.removeChild(cell);
                  if (row.cells && row.cells.length === 1) {
                    row.cells[0].style.width = '100%';
                    row.cells[0].setAttribute('width', '100%');
                  }
                };
                markInnerBlocks(body);
                Array.prototype.forEach.call(
                  body.querySelectorAll(DRAGGABLE_SELECTOR),
                  (node) => {
                    if (isLockedLayoutCard(node) || isFooterLayoutNode(node)) {
                      return;
                    }
                    const canDrag = node.nodeName !== 'TD' && node.nodeName !== 'TR';
                    const stacks =
                      node.nodeName === 'TABLE' ? stackCellsOf(node) : [];
                    attachCardTools(node, removeLayoutCard, canDrag, true);
                    if (stacks.length < 2) {
                      return;
                    }
                    stacks.forEach((cell) => {
                      if (cell.querySelector('.mcc-col-delete')) {
                        return;
                      }
                      const tools = doc.createElement('span');
                      tools.className = 'mcc-card-tools';
                      tools.setAttribute('contenteditable', 'false');
                      tools.setAttribute('data-mce-bogus', 'all');
                      const del = doc.createElement('button');
                      del.type = 'button';
                      del.className = 'mcc-card-delete mcc-col-delete';
                      del.setAttribute('contenteditable', 'false');
                      del.setAttribute('data-mce-bogus', 'all');
                      del.setAttribute('title', 'Remove this column');
                      del.appendChild(doc.createTextNode('×'));
                      editor.dom.bind(del, 'mousedown', (event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      });
                      editor.dom.bind(del, 'click', (event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        removeStackCell(cell);
                        editor.undoManager.add();
                        editor.nodeChanged();
                        editor.fire('change');
                      });
                      tools.appendChild(del);
                      editor.dom.setStyle(cell, 'position', 'relative');
                      cell.insertBefore(tools, cell.firstChild);
                    });
                  }
                );
              };
              editor.on('click', (event) => {
                if (
                  editor.dom.getParent(
                    event.target,
                    '.mcc-card-delete, .mcc-card-tools, .mcc-card-handle, .mcc-card-move'
                  )
                ) {
                  return;
                }
                const slot = editor.dom.getParent(
                  event.target,
                  '[data-logo-slot="pharmacy"]'
                );
                const icon = editor.dom.getParent(event.target, 'img[data-icon="rx"]');
                if (!slot && !icon) {
                  return;
                }
                event.preventDefault();
                window.setTimeout(() => {
                  if (icon) {
                    self.openSectionIconDialog(icon);
                    return;
                  }
                  self.openPharmacyLogoDialog();
                }, 0);
              });
              editor.on('CloseWindow', () => {
                window.setTimeout(() => {
                  self.finishPharmacyLogoPick();
                  self.finishSectionIconPick();
                }, 50);
              });
              const scheduleCanvasRefresh = (forceDom) => {
                if (self.canvasTimer) {
                  window.clearTimeout(self.canvasTimer);
                }
                self.canvasTimer = window.setTimeout(() => {
                  self.canvasTimer = null;
                  if (self.unmounted || !self.editor) {
                    return;
                  }
                  self.suppressChange = true;
                  [
                    () => preset === 'email' && restorePoweredBy(self.editor),
                    () => forceDom && forceEmailCanvasDom(self.editor),
                    decorateDeletableSections,
                    () => attachSmoothCanvasDrag(self.editor),
                    () => trackHoverBlock(self.editor),
                  ].forEach((step) => {
                    try {
                      step();
                    } catch (err) {
                      console.error('Email canvas refresh step failed', err);
                    }
                  });
                  self.releaseSuppressChange();
                }, 16);
              };
              self.scheduleCanvasRefresh = scheduleCanvasRefresh;
              editor.on('SetContent', (event) => {
                if (self.suppressChange || (event && event.selection)) {
                  return;
                }
                scheduleCanvasRefresh(true);
              });
              editor.on('undo redo', () => {
                scheduleCanvasRefresh(false);
              });
              editor.on('GetContent', (event) => {
                if (!event || !event.content) {
                  return;
                }
                event.content = String(event.content)
                  .replace(
                    /<(span|button)\b[^>]*class="[^"]*\bmcc-card-(?:tools|delete|handle|move)\b[^"]*"[^>]*>[\s\S]*?<\/\1>/gi,
                    ''
                  )
                  .replace(/>([^<]*)</g, (full, text) => {
                    const cleaned = text
                      .replace(/::/g, '')
                      .replace(/×/g, '')
                      .replace(/&times;/gi, '');
                    return `>${cleaned}<`;
                  })
                  .replace(/\sdata-mcc-dragging="[^"]*"/gi, '')
                  .replace(/\sdata-mcc-hover="[^"]*"/gi, '')
                  .replace(/<div[^>]*id=["']mcc-drop-line["'][^>]*>[\s\S]*?<\/div>/gi, '')
                  .replace(
                    /<div[^>]*id=["']mcc-resize-box["'][^>]*>(?:\s*<div[^>]*>[^<]*<\/div>)*\s*<\/div>/gi,
                    ''
                  )
                  .replace(/\sdata-mcc-resizing="[^"]*"/gi, '')
                  .replace(/<[^>]*data-mcc-ghost[^>]*>[\s\S]*?<\/[^>]+>/gi, '')
                  .replace(/<colgroup\b[^>]*>[\s\S]*?<\/colgroup>/gi, '')
                  .replace(/<col\b[^>]*\/?>/gi, '');
              });
            }
            editor.on('init', () => {
              if (self.unmounted || gen !== self.initGen) {
                try {
                  editor.remove();
                } catch (_err) {
                  // ignore
                }
                return;
              }
              self.ready = true;
              self.lastEmitted = self.props.value || '';
              self.suppressChange = true;
              try {
                editor.setContent(self.lastEmitted);
              } catch (_err) {
                // ignore
              }
              self.releaseSuppressChange();
              self.paintAccountLogo();
              if (preset === 'sms' || preset === 'email') {
                attachImageDeleteButton(editor);
              }
              if (typeof self.scheduleCanvasRefresh === 'function') {
                self.scheduleCanvasRefresh(true);
              }
              if (self.props.disabled) {
                try {
                  editor.mode.set('readonly');
                } catch (_err) {
                  // ignore
                }
              }
            });
            const emitChange = () => {
              if (self.emitTimer) {
                window.clearTimeout(self.emitTimer);
              }
              self.emitTimer = window.setTimeout(() => {
                self.emitTimer = null;
                self.emitCurrentContent();
              }, 40);
            };
            editor.on('change input keyup undo redo paste', emitChange);
          },
        };
        if (enableImages) {
          config.image_title = true;
          config.image_dimensions = preset === 'email' ? false : undefined;
          config.image_caption = preset !== 'email';
          config.image_advtab = true;
          config.image_uploadtab = true;
          config.automatic_uploads = true;
          config.file_picker_types = 'image';
          config.paste_data_images = true;
          config.extended_valid_elements =
            preset === 'email'
              ? EMAIL_EXTENDED_ELEMENTS
              : 'img[class|src|border=0|alt|title|hspace|vspace|width|height|align|onmouseover|onmouseout|name|style],iframe[src|width|height|name|align|frameborder|scrolling|allow|allowfullscreen],video[*],source[*],figure,figcaption';
          config.images_upload_handler = (blobInfo) => uploadBlob(blobInfo, uploader);
          config.file_picker_callback = (callback, _value, meta) => {
            if (meta.filetype !== 'image') {
              return;
            }
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/png,image/jpeg,image/gif,image/webp';
            input.onchange = () => {
              const file = input.files && input.files[0];
              if (!file) {
                return;
              }
              uploader(file)
                .then((url) => {
                  callback(url, { title: file.name, alt: file.name });
                })
                .catch((error) => {
                  window.alert(
                    (error && error.message) || 'Unable to upload that image.'
                  );
                });
            };
            input.click();
          };
        }
        if (self.unmounted || gen !== self.initGen) {
          return;
        }
        tinymce.init(config).then((editors) => {
          if ((self.unmounted || gen !== self.initGen) && editors && editors.length) {
            editors.forEach((editor) => {
              try {
                editor.remove();
              } catch (_err) {
                // ignore
              }
            });
          }
        }).catch(() => {
          if (!self.unmounted) {
            self.setState({
              loadError: 'Unable to load the HTML editor.',
            });
          }
        });
      })
      .catch((error) => {
        if (!self.unmounted) {
          self.setState({
            loadError:
              (error && error.message) ||
              'Unable to load the HTML editor.',
          });
        }
      });
  };

  handleFallbackChange = (event) => {
    const target = event && event.target;
    if (this.props.onChange && target) {
      this.props.onChange(target.value);
    }
  };

  render() {
    const { label, value, fillHeight, compact, error, disabled, required } = this.props;
    const classNames = ['feature-html-editor'];
    if (fillHeight) {
      classNames.push('feature-html-editor-fill');
    }
    if (compact) {
      classNames.push('feature-html-editor-compact');
    }
    if (error) {
      classNames.push('feature-html-editor-error-border');
    }
    return (
      <div className={classNames.join(' ')}>
        {label ? (
          <label className="feature-html-editor-label" htmlFor={this.editorId}>
            {label}
            {required ? <span className="feature-html-editor-required"> *</span> : null}
          </label>
        ) : null}
        {this.state.loadError ? (
          <textarea
            id={this.editorId}
            className="feature-html-editor-fallback"
            value={value || ''}
            onChange={this.handleFallbackChange}
            rows={10}
            disabled={Boolean(disabled)}
          />
        ) : (
          <textarea id={this.editorId} defaultValue={value || ''} />
        )}
        {this.state.loadError ? (
          <p className="feature-html-editor-error">{this.state.loadError}</p>
        ) : null}
      </div>
    );
  }
}

HtmlEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  logoUrl: PropTypes.string,
  label: PropTypes.string,
  fillHeight: PropTypes.bool,
  compact: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  required: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  enableImages: PropTypes.bool,
  uploadImage: PropTypes.func,
  tokens: PropTypes.array,
  layoutCards: PropTypes.array,
  preset: PropTypes.string,
  onSelectToken: PropTypes.func,
  onInsertCard: PropTypes.func,
};

HtmlEditor.defaultProps = {
  value: '',
  onChange: () => {},
  logoUrl: '',
  label: '',
  fillHeight: false,
  compact: false,
  disabled: false,
  error: false,
  required: false,
  height: null,
  enableImages: true,
  uploadImage: null,
  tokens: [],
  layoutCards: [],
  preset: '',
  onSelectToken: null,
  onInsertCard: null,
};

export default HtmlEditor;



// WEBPACK FOOTER //
// ./src/components/feature-catalog-admin/HtmlEditor.js